import {GeneralStatusEnum} from "../../enums/GeneralStatusEnum";
import {PlanLevelEnum} from "../../enums/PlanLevelEnum";
import {FeatureTypeEnum} from "../../enums/FeatureTypeEnum";
import {FeatureStatusEnum} from "../../enums/FeatureStatusEnum";

enum EntityTag {
    Organisation = 'ORGANISATION',
    Brand = 'BRAND',
    BrandEquity = 'BRAND_EQUITY',
    QuestionTemplate = 'QUESTION_TEMPLATE',
    Survey = 'SURVEY',
    SurveyCohort = 'SURVEY_COHORT',
    AIExperts = 'AI_EXPERTS',
    AIDocuments = 'AI_DOCUMENTS',
    AIConversations = 'AI_CONVERSATIONS',
    Messages = 'MESSAGES',
    CustomBrandChart = 'CUSTOM_BRAND_CHART',
    TICKETS = 'TICKETS',
    ADMIN_TICKETS = 'ADMIN_TICKETS',
    ADMIN_TICKET_STATS = 'ADMIN_TICKET_STATS',
    TICKET_CATEGORIES = 'TICKET_CATEGORIES',
}

interface IAuth {
    access_token: string;
}

interface IOrganisation {
    id: number;
    name: string;
    billingEmail?: string;
    organisationFeaturesDto?: string;
    plan: PlanLevelEnum;
    status?: string;
    hash?: string;
    createdAt?: string;
    updatedAt?: string;
    isMock?: boolean; // Flag to identify mock/test data
    orgType?: 'standard' | 'agency';
    benchmarkStatus?: 'pending' | 'included' | 'excluded';
    // Concierge mode: automated free-trial emails are held as drafts instead
    // of being delivered, so they can be sent personally instead.
    suppressTrialEmails?: boolean;
    // Charter Cohort (SYR-106). charterJoinedAt non-null means this org is, or
    // once was, a Charter brand — the durable predicate, not the plan code.
    charterJoinedAt?: string | null;
    charterSendCommitmentEndsAt?: string | null;
    charterRenewalPriceCents?: number | null;
    // SYR-105 — research (The Hypothesis and Auto-Concierge) reads this and
    // silently does nothing when it is null, which was the state of every
    // admin-created org until it became editable.
    websiteUrl?: string | null;
    identityVerificationStatus?: 'unverified' | 'verified' | 'rejected';
    identityVerifiedAt?: string | null;
    identityVerificationNotes?: string | null;
    businessModel?: string;
    productCategory?: string;
    templateType?: string;
    productTypeOverride?: string;
    // Admin-specific fields
    userCount?: number;
    brandCount?: number;
    surveyCount?: number;
    responseCount?: number;
    lastActivity?: string;
    subscription?: {
        plan: string;
        status: string;
        expiresAt?: string;
        foundingCohort?: boolean;
        // Present for trials; lets the list show "day N of M" with no extra call.
        trialStart?: string;
        trialEnd?: string;
    };
    // Agency relationship (admin org-details endpoint) — null when not agency-managed
    agency?: {
        agencyClientId: number;
        agencyOrganisationId: number;
        agencyName: string | null;
        status: string;
        billingResponsibility: string;
        invitedAt: string | null;
        acceptedAt: string | null;
    } | null;
}

interface IApiUser {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    roles: string | string[]; // Can be JSON string or parsed array depending on endpoint
    status: GeneralStatusEnum;
    organisation: IOrganisation;
    createdAt?: string;
    updatedAt?: string;
}

interface IUpdateApiUser {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    roles: string;
    status: GeneralStatusEnum;
}

interface ICreateApiUser {
    email: string;
    firstName: string;
    lastName: string;
    roles: string;
}

interface ICreateOrganisationWithApiUser {
    organisationName: string;
    websiteUrl?: string;
    firstName: string;
    lastName: string;
    email: string;
    acceptTerms?: boolean;
    // SYR-109: signed waitlist invite JWT; the API's one exemption while
    // PUBLIC_SIGNUP_ENABLED is off.
    inviteToken?: string;
    // Mode A: server re-resolves templateType from productCategory (+ model).
    productCategory?: string;
    businessModel?: string;
    templateType?: string;
    productType?: string;
}

interface ICreateWaitlistEntry {
    firstName: string;
    lastName: string;
    email: string;
    businessName: string;
    websiteUrl?: string;
    businessModel?: string;
    productCategory?: string;
    notes?: string;
}

interface IWaitlistEntry {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    businessName: string;
    websiteUrl: string | null;
    businessModel: string | null;
    productCategory: string | null;
    productType: string | null;
    source: 'signup' | 'charter';
    status: string;
    notes: string | null;
    inviteCount: number;
    lastInvitedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

interface IWaitlistStats {
    status: string;
    count: number;
}

// Charter Cohort (SYR-106)
type CharterCustomerCountBand = 'under_500' | '500_2000' | '2000_10000' | '10000_plus';
type CharterReachableListBand = 'none' | CharterCustomerCountBand;
type CharterSendWithin30Days = 'yes' | 'yes_with_signoff' | 'no';
type CharterApplicationStatus = 'pending' | 'accepted' | 'held' | 'unsupported';

interface ICharterPageConfig {
    enabled: boolean;
    introMarkdown: string;
    termsMarkdown: string;
    confirmationMarkdown: string;
}

interface ISubmitCharterApplication {
    brandName: string;
    websiteUrl: string;
    contactName: string;
    email: string;
    roleAtBrand: string;
    productCategory: string;
    businessModel?: string;
    emailPlatform?: string;
    customerCountBand: CharterCustomerCountBand;
    reachableListBand: CharterReachableListBand;
    canSendWithin30Days: CharterSendWithin30Days;
    unansweredQuestion: string;
    // Honeypot. Always empty for a real applicant.
    companyWebsite?: string;
}

// SYR-107 — the visitor-facing lead generator.
//
// `state` is a small closed set and deliberately NOT a mirror of the server's
// internal status: a blocked request and a queued one both arrive as 'review',
// because a rejection that explains itself is one you can iterate against.
type PublicHypothesisState =
    | 'pin_sent'
    | 'pin_invalid'
    | 'review'
    | 'running'
    | 'ready'
    | 'insufficient_data'
    | 'failed'
    // Something upstream was unavailable. Deliberately vague to the visitor:
    // which service, and why, is our business and not theirs.
    | 'delayed'
    | 'link_expired'
    | 'unavailable';

interface IPublicHypothesisConfig {
    // The kill switch for the RUN, not the form. Off, the form still collects,
    // looking exactly as it does live, and each submission parks for a manual
    // run; PIN and verify answer 'unavailable' independently.
    enabled: boolean;
    introMarkdown: string;
    // The exact wording whose hash is pinned onto every lead at submit.
    consentMarkdown: string;
    disabledMarkdown: string;
    // How long to poll before handing over to the email.
    waitMinutes: number;
}

interface IPublicHypothesisRequestResult {
    state: PublicHypothesisState;
    brandName?: string | null;
    targetDomain?: string | null;
}

interface IPublicHypothesisVerifyResult {
    state: PublicHypothesisState;
    token?: string;
}

interface IPublicHypothesisStatus {
    state: PublicHypothesisState;
    stage?: string | null;
    queuePosition?: number;
    elapsedSeconds?: number;
    brandName?: string | null;
}

interface IPublicHypothesisElementReading {
    element: string;
    signal: 'STRENGTH' | 'WATCH' | 'LIKELY_CONSTRAINT' | 'INSUFFICIENT_SIGNAL';
    note: string;
    // YouTube id for this element's explainer, when one has been recorded.
    // Null while the library is still being built, which is most of them.
    videoId?: string | null;
    // Heading for the player, resolved server-side so the wording is editable
    // without a deploy.
    videoTitle?: string | null;
}

interface IPublicHypothesisResult {
    state: PublicHypothesisState;
    brandName?: string | null;
    generatedAt?: string | null;
    headline?: string;
    claim?: string;
    constraintElement?: string;
    elementReadings?: IPublicHypothesisElementReading[];
    competitorsAnalysed?: string[];
    closingQuestion?: string;
    sourcesSummary?: string;
    // Present only when the page no longer matches the email that announced
    // it, and never for internal testers. Carries the element the email named.
    rerunNotice?: { previousElement: string };
}

// SYR-107 — the public Hypothesis lead queue.
//
// `status` and `reasonCode` are kept apart on purpose: "we would not run this"
// and "we could not run this" are different conversations, and a rate-limited
// lead is worth revisiting tomorrow while a domain mismatch never is.
interface IHypothesisLead {
    id: number;
    email: string;
    emailDomain: string | null;
    submittedUrl: string;
    targetDomain: string | null;
    derivedBrandName: string | null;
    status:
        | 'PENDING_PIN'
        | 'VERIFIED'
        | 'RUNNING'
        | 'READY'
        | 'QUEUED_REVIEW'
        | 'BLOCKED'
        | 'INSUFFICIENT_DATA'
        | 'FAILED';
    reasonCode: string | null;
    subjectId: number | null;
    hypothesisId: number | null;
    // True when this lead was served an already-formed hypothesis and cost
    // nothing. Keeps runs and leads from being conflated in reporting.
    servedCached: boolean;
    verifiedAt: string | null;
    consentAt: string | null;
    resultEmailSentAt: string | null;
    unsubscribedAt: string | null;
    crmCompanyId: number | null;
    reviewedAt: string | null;
    reviewNotes: string | null;
    createdAt: string;
}

interface IHypothesisLeadPage {
    items: Array<IHypothesisLead & {
        // What this lead cost us in AI spend. Priced with the same model table
        // as the platform usage screen, so the two always agree.
        aiTokens: number;
        aiCalls: number;
        aiCostUsd: number;
        // Ours, testing against a real brand in production. Derived from the
        // internal-domain list, so it reflects that list as it stands now.
        isInternal: boolean;
        // Why a run actually failed, off the job. `reasonCode` only says
        // 'run_failed', which a timeout, a bad key and a rejected element pick
        // all share; this is the message that separates them. attemptCount
        // says whether the failure repeated (deterministic) or happened once.
        lastError: string | null;
        attemptCount: number;
        progressStage: string | null;
        // Stored research the hypothesis can be re-formed from without a
        // crawl. Null means a re-form has nothing to work with.
        researchCompletedAt: string | null;
        // The job's own status. A re-form leaves the lead READY while the job
        // cycles, so this is the only signal that anything is happening.
        jobStatus: string | null;
    }>;
    total: number;
    page: number;
    limit: number;
    pageCostUsd: number;
}

// SYR-107 — the research held against a subject, as the admin queue shows it.
// Mirrors HypothesisEvidence on the API; anything optional here is optional
// there because the provider that fills it can be absent or degraded.
interface IHypothesisEvidence {
    websiteReached: boolean;
    pagesFetched: Array<{ url: string; chars: number }>;
    place: {
        found: boolean;
        name?: string;
        rating?: number;
        totalReviews?: number;
        listingCount?: number;
        aggregateRating?: number;
        aggregateReviewCount?: number;
        sampledLocations?: Array<{ placeId: string; name: string; reviewsCollected: number }>;
    };
    reviewTextsCollected: number;
    reviewSource: 'serpapi' | 'places_only' | 'none';
    providersDegraded?: boolean;
    degradedReasons?: string[];
    // A paid renderer read the page after the free routes could not.
    rendered?: { provider: 'firecrawl'; chars: number };
    // The Shopify storefront API stood in for a client-rendered site.
    catalogue?: {
        platform: 'shopify';
        url: string;
        productCount: number;
        excludedCount?: number;
        country?: string;
        currency?: string;
        shipsToCountries?: string[];
    };
    ecosystemSources?: Array<{ source: string; url: string; chars: number }>;
    // `brand` is the gated half; `context` is competitor research, which is
    // never gated on because it says nothing about the subject.
    scoreBreakdown?: { brand: number; context: number };
    competitors?: Array<{
        name: string;
        website?: string;
        rating?: number;
        totalReviews?: number;
        pagesFetched: number;
    }>;
    competitiveLandscape?: string;
    evidenceNotes?: string;
}

interface IHypothesisSubjectDetail {
    subject: {
        id: number;
        name: string;
        websiteUrl: string | null;
        subjectType: string;
        origin: string;
        publicFootprint: string | null;
        customerSentiment: string | null;
        competitiveLandscape: string | null;
        positioning: string | null;
        targetCustomers: string | null;
        keyCompetitors: string | null;
        productsServices: string | null;
        markets: string | null;
        espGuess: string | null;
        customerBaseSignal: string | null;
        researchedAt: string | null;
    } | null;
    job: {
        id: number;
        status: string;
        evidenceScore: number | null;
        evidenceJson: IHypothesisEvidence | null;
        researchCompletedAt: string | null;
        lastError: string | null;
    } | null;
    hypothesis: {
        headline: string;
        claim: string;
        constraintElement: string;
        sourcesSummary: string;
        elementReadings?: Array<{ element: string; signal: string; note: string }>;
    } | null;
}

interface IHypothesisDomainPolicy {
    id: number;
    domain: string;
    policy: 'auto' | 'manual' | 'blocked';
    // Load-bearing: nobody remembers in six weeks why a domain went on the
    // list, and an unexplained block looks like a bug.
    note: string | null;
    createdAt: string;
    updatedAt: string;
}

interface ICharterApplication {
    id: number;
    brandName: string;
    websiteUrl: string;
    contactName: string;
    email: string;
    emailDomain: string | null;
    isFreeMailDomain: boolean;
    roleAtBrand: string;
    productCategory: string | null;
    businessModel: string | null;
    resolvedTemplateType: string | null;
    qualificationSupported: boolean;
    emailPlatform: string | null;
    customerCountBand: CharterCustomerCountBand;
    reachableListBand: CharterReachableListBand;
    canSendWithin30Days: CharterSendWithin30Days;
    unansweredQuestion: string;
    status: CharterApplicationStatus;
    reviewedAt: string | null;
    reviewedBy: number | null;
    reviewNotes: string | null;
    crmCompanyId: number | null;
    waitlistEntryId: string | null;
    submittedAt: string;
    requestIp: string | null;
    priorSubmissionCount: number;
}

interface ICharterApplicationStats {
    pending: number;
    accepted: number;
    held: number;
    unsupported: number;
    total: number;
}

interface IFeatureSpecification {
    status: FeatureStatusEnum;
    value: any;
    unlockLevel: PlanLevelEnum;
}

interface IFeature {
    name: FeatureTypeEnum,
    specification: IFeatureSpecification;
}

interface IOrganisationFeatures {
    features: IFeature[];
}

// Brand Equity Types
interface IBrand {
    id: number;
    name: string;
    hash: string;
    logoUrl?: string;
    primaryColor: string;
    templateType?: string | null;
    organisationId: number;
    productType?: string;
    businessTypeId?: number | null;
    benchmarkProductTypeId?: number | null;
    createdAt: string;
    updatedAt: string;
}

export interface IBenchmarkBusinessType {
    id: number;
    name: string;
    slug: string;
    displayOrder: number;
    isActive: boolean;
}

export interface IBenchmarkProductTypeConfig {
    id: number;
    name: string;
    slug: string;
    displayOrder: number;
    isActive: boolean;
}

interface IDimensionData {
    score: number;
    weight: number;
    trend: string;
    sparkline: number[];
    subDimensions: {
        [key: string]: {
            score: number;
            questions: string[];
            metrics?: {
                [key: string]: number;
            };
        };
    };
}

interface IBrandEquity {
    brandName: string;
    brandHash: string;
    overallScore: number;
    overallTrend: string;
    dimensions: {
        loyalty: IDimensionData;
        perceivedQuality: IDimensionData;
        associations: IDimensionData;
        awareness: IDimensionData;
    };
    calculatedAt: string;
}

interface ITrendData {
    date: string;
    overallScore: number;
    loyalty: number;
    perceivedQuality: number;
    associations: number;
    awareness: number;
}

interface IQuestionTemplate {
    id: number;
    code: string;
    text: string;
    category: string;
    displayOrder: number;
    isCore: boolean;
    isRequired: boolean;
    questionTypeId: number;
    useBrandPlaceholder: boolean;
    scoringType: string;
    status: 'active' | 'inactive';
    templateType: string;
    createdAt: string;
    updatedAt: string;
}

interface IUpdateQuestionTemplate {
    text?: string;
    category?: string;
    displayOrder?: number;
    isCore?: boolean;
    isRequired?: boolean;
    questionTypeId?: number;
    useBrandPlaceholder?: boolean;
    status?: 'active' | 'inactive';
    scoringType?: string;
}

// Survey Types
interface ISurveyTheme {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    cardColor?: string;
    accentColor?: string;
}

interface ISurveySettings {
    id: number;
    surveyId: number;
    questionsPerResponse: number;
    rotationStrategy: 'sequential' | 'random' | 'balanced' | 'adaptive';
    theme: string; // JSON string
    showProgressBar: boolean;
    animationSpeed: number;
    allowSkip: boolean;
    allowBackNavigation: boolean;
    persistProgress: boolean;
    expirationHours: number;
    completionTitle: string;
    completionSubtitle: string;
    completionButtonText: string;
    completionButtonLink?: string;
    showCompletionButton: boolean;
    completionSecondaryButtonText?: string;
    completionSecondaryButtonLink?: string;
    // The reward on the thank-you page (SYR-105 phase 2). showReward hides it
    // without deleting it, mirroring the two completion button toggles.
    showReward?: boolean;
    discountCode?: string | null;
    offerDescription?: string | null;
    offerTerms?: string | null;
    showCompletionSecondaryButton?: boolean;
    collectEmail: boolean;
    emailRequired: boolean;
    emailPlaceholder: string;
    npsRequired?: boolean;
    showWelcomePage?: boolean;
    finalQuestionId?: number | null;
    createdAt: string;
    updatedAt: string;
}

interface ISurvey {
    id: number;
    hash: string;
    name: string;
    description: string;
    status: 'draft' | 'active' | 'paused' | 'completed';
    brandId: number;
    organisationId: number;
    cohortId: number;
    countryCode?: string; // ISO 3166-1 alpha-2 country code
    languageCode?: string; // ISO 639-1 language code
    startsAt: string;
    endsAt: string;
    createdAt: string;
    updatedAt: string;
    brand?: IBrand;
    settings?: ISurveySettings;
    cohort?: ISurveyCohort;
    accessCount?: number;
    responseCount?: number;
}

interface ICreateSurveyPayload {
    brandId: number;
    name: string;
    description?: string;
    countryCode?: string; // ISO 3166-1 alpha-2 country code
    languageCode?: string; // ISO 639-1 language code
    status?: 'draft' | 'active' | 'paused' | 'completed';
    startsAt?: string;
    endsAt?: string;
    questionsPerResponse?: number;
    rotationStrategy?: 'sequential' | 'random' | 'balanced' | 'adaptive';
    theme?: ISurveyTheme;
    showProgressBar?: boolean;
    animationSpeed?: number;
    allowSkip?: boolean;
    allowBackNavigation?: boolean;
    persistProgress?: boolean;
    expirationHours?: number;
    completionTitle?: string;
    completionSubtitle?: string;
    completionButtonText?: string;
    completionButtonLink?: string;
    showCompletionButton?: boolean;
    completionSecondaryButtonText?: string;
    completionSecondaryButtonLink?: string;
    // The reward on the thank-you page (SYR-105 phase 2). showReward hides it
    // without deleting it, mirroring the two completion button toggles.
    showReward?: boolean;
    discountCode?: string | null;
    offerDescription?: string | null;
    offerTerms?: string | null;
    showCompletionSecondaryButton?: boolean;
    collectEmail?: boolean;
    emailRequired?: boolean;
    emailPlaceholder?: string;
    npsRequired?: boolean;
    showWelcomePage?: boolean;
    cohortId: number;
}

// Survey Cohort Types
interface ISurveyCohort {
    id: number;
    code: string;
    name: string;
    displayName: string;
    description?: string;
    surveyCount?: number;
    isActive?: boolean;
    cohortType?: 'system' | 'organisation' | 'brand';
    parentCohort?: ISurveyCohort;
    organisation?: IOrganisation;
    brand?: IBrand;
    hash?: string;
    createdAt?: string;
    updatedAt?: string;
}

// Admin Survey Cohort Response (with additional admin-specific fields)
interface IAdminSurveyCohortResponse extends ISurveyCohort {
    surveyCount: number;
    organisationName?: string | null;
    brandName?: string | null;
}

// Survey Response Types
interface IQuestion {
    id: number;
    hash: string;
    surveyId: number;
    questionTypeId: number;
    text: string;
    questionTemplateId: number;
    code: string;
    category: string;
    displayOrder: number;
    isRequired: boolean;
    isCore: boolean;
    config: any;
    createdAt: string;
}

interface IQuestionResponse {
    id: number;
    surveyResponseId: number;
    questionId: number;
    numericValue: number | null;
    textValue: string | null;
    responseTimeSeconds: number | null;
    createdAt: string;
    question: IQuestion;
    selectedOptions: any[];
}

interface ISurveyResponse {
    id: number;
    hash: string;
    surveyId: number;
    participantEmail?: string;
    participantIdentifier?: string;
    submittedAt: string;
    ipAddress: string;
    userAgent: string;
    questionResponses: IQuestionResponse[];
}

interface ISurveyResponseSummary {
    totalResponses: number;
    completedResponses: number;
    completionRate: number;
    averageCompletionTime: number;
    uniqueParticipants: number;
    totalAccesses: number;
}

// Admin Types
interface IAdminBrandResponse {
    id: number;
    hash: string;
    name: string;
    organisationId: number;
    organisationName: string;
    status: string;
    createdAt: string;
    surveyCount: number;
    responseCount: number;
    isMock: boolean; // Flag to identify mock/test data (inherited from organization)
}

// SYR-78 — Brand Knowledge Base
export interface IBrandKnowledgeProfile {
    brandId: number;
    organisationId: number;
    positioning: string | null;
    targetCustomers: string | null;
    keyCompetitors: string | null;
    toneOfVoice: string | null;
    brandGoals: string | null;
    productsServices: string | null;
    additionalContext: string | null;
    updatedAt: string | null;
}

export interface IUpdateBrandKnowledge {
    positioning?: string | null;
    targetCustomers?: string | null;
    keyCompetitors?: string | null;
    toneOfVoice?: string | null;
    brandGoals?: string | null;
    productsServices?: string | null;
    additionalContext?: string | null;
}

// SYR-90 — The Hypothesis card (element names arrive as display names)
export type HypothesisSignal =
    | 'STRENGTH'
    | 'WATCH'
    | 'LIKELY_CONSTRAINT'
    | 'INSUFFICIENT_SIGNAL';

export interface IHypothesisElementReading {
    element: string;
    signal: HypothesisSignal;
    note: string;
}

export interface IHypothesisCard {
    exists: boolean;
    status?: 'ACTIVE' | 'CONFIRMED' | 'OVERTURNED' | 'EXPIRED';
    headline?: string;
    claim?: string;
    /** Pre-Phase-6 hypotheses only — superseded by elementReadings. */
    evidenceBullets?: string[];
    /** Phase 6C — the element scorecard (empty for pre-Phase-6 rows). */
    elementReadings?: IHypothesisElementReading[];
    competitorsAnalysed?: string[];
    closingQuestion?: string;
    constraintElement?: string;
    darkHorseElement?: string | null;
    sourcesSummary?: string;
    responseCount?: number;
    requiredResponses?: number;
    formedAt?: string;
    revealHeadline?: string;
    revealBody?: string;
    measuredConstraint?: string;
    resolvedAt?: string | null;
    responseCountAtResolution?: number | null;
}

export interface IBrandKnowledgeSynthesis {
    markdown: string;
    tokenCount: number;
    truncated: boolean;
    isEmpty: boolean;
}

export interface IBrandKnowledgeDocument {
    id: number;
    sourceType: 'upload' | 'url_scrape';
    sourceRef: string;
    tokenCount: number;
    mimeType: string | null;
    fileSize: number | null;
    scrapedAt: string | null;
    hasOriginal: boolean;
    hasRawText: boolean;
    includeInContext: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface IBrandKnowledgeDocumentText {
    id: number;
    sourceType: 'upload' | 'url_scrape';
    sourceRef: string;
    extractedText: string | null;
    rawText: string | null;
    tokenCount: number;
}

export interface IBrandFieldSuggestions {
    positioning?: string;
    productsServices?: string;
    targetCustomers?: string;
    keyCompetitors?: string;
    toneOfVoice?: string;
    brandGoals?: string;
}

export interface IBrandKnowledgeInspection {
    brandId: number;
    brandName: string;
    organisationId: number;
    organisationName: string | null;
    profile: IBrandKnowledgeProfile | null;
    documents: IBrandKnowledgeDocument[];
    synthesised: IBrandKnowledgeSynthesis;
}

interface IAdminSurveyResponse {
    id: number;
    hash: string;
    name: string;
    brandId: number;
    brandName: string;
    organisationId: number;
    organisationName: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    startsAt: string;
    endsAt: string;
    responseCount: number;
    completionRate: number;
    averageCompletionTime: number;
    accessCount: number;
    description?: string;
    cohortId?: number;
    cohortName?: string;
    countryCode?: string;
    languageCode?: string;
    isMock: boolean; // Flag to identify mock/test data (inherited from organization)
    // Response Integrity (SYR-48 — Phase 5)
    panelPartner?: string | null;
    detectorProfile?: string | null;
    llmBudgetOverrideUsd?: number | null;
    // Optional fields to make it compatible with ISurvey
    brand?: IBrand;
    settings?: ISurveySettings;
    cohort?: ISurveyCohort;
}

// Question Coverage Types
interface ICoverageStatement {
    questionId: number;
    questionHash: string;
    questionCode: string;
    questionText: string;
    category: string;
    isCore: boolean;
    displayOrder: number;
    responseCount: number;
}

interface ICoverageCategory {
    category: string;
    totalResponses: number;
    statementCount: number;
    statements: ICoverageStatement[];
}

interface ICoverageSummary {
    totalStatements: number;
    coreStatements: number;
    nonCoreStatements: number;
    totalResponses: number;
    completedResponses: number;
    totalCategories: number;
    targetResponsesPerCategory: number;
    categoryBalanceScore: number;
    statementBalanceScore: number;
}

interface IQuestionCoverageResponse {
    summary: ICoverageSummary;
    categories: ICoverageCategory[];
    statements: ICoverageStatement[];
}

// Brand Equity Calculation Types
enum PeriodEnum {
    THIRTY_DAYS = '30_days',
    SIXTY_DAYS = '60_days',
    NINETY_DAYS = '90_days',
    ONE_TWENTY_DAYS = '120_days',
    ONE_EIGHTY_DAYS = '180_days',
    THREE_SIXTY_FIVE_DAYS = '365_days',
    ALL_TIME = 'all_time',
    DATE_RANGE = 'date_range',
}

interface ICalculateBrandEquityRequest {
    brandIds?: number[];
    cohortIds?: number[]; // Survey cohort IDs to filter by, null/undefined = all cohorts
    countryCodes?: string[]; // Country codes to filter by, null/undefined = all countries
    period?: PeriodEnum; // Period selection: 30_days, 60_days, 120_days, or date_range
    dateFrom?: string; // ISO date string - only used when period is 'date_range'
    dateTo?: string; // ISO date string - only used when period is 'date_range'
    skipAiAnalysis?: boolean; // Skip AI element analysis for faster response
    forceRecalculate?: boolean; // Bypass cache and force fresh calculation
    skipRecommendations?: boolean; // Skip token-heavy Briefing AI recommendations (locked tiers)
}

interface IQuestionCategoryScore {
    category: string;
    meanScore: number | null;
    responseCount: number;
    standardDeviation: number | null;
    aiSummary?: string;
    locked?: boolean; // True if element is restricted by subscription tier
    classificationLabel?: string;
    input?: Array<{
        id: string;
        text: string;
        scoringType: string;
        questionTypeId?: number;
        responseCount: number;
        meanScore: number;
        polarityValue?: number;
        polarityPercentage?: number;
        intensityValue?: number;
        intensityPercentage?: number;
        brandPowerPercentage?: number;
        controversyValue?: number;
        controversyPercentage?: number;
        scoreDistribution?: { [score: string]: number } | null;
    }>;
}

interface ILatentElementInput {
    name: string;
    type: 'QC' | 'LE'; // Question Category or Latent Element
    value: number | null; // null when no data exists for this input
    // Note: weight and contribution removed for commercial sensitivity
}

interface ILatentElementScore {
    element: string;
    score: number | null;
    inputs: ILatentElementInput[];
    aiSummary?: string;
    locked?: boolean; // True if element is restricted by subscription tier
    classificationLabel?: string;
}

interface IBrandEquityFilters {
    brandIds?: number[];
    cohortIds?: number[];
    countryCodes?: string[];
    period?: PeriodEnum;
    dateFrom?: string;
    dateTo?: string;
}

interface IBrandEquityScoreResponse {
    questionCategoryScores: IQuestionCategoryScore[];
    latentElementScores: ILatentElementScore[];
    overallBrandEquityScore: number;
    overallClassificationLabel?: string;
    totalResponses: number;
    calculatedAt: string;
    insufficientSampleSize?: boolean;
    filters: IBrandEquityFilters;
}

interface IAvailableSurvey {
    id: number;
    title: string;
    organisationId: number;
}

// API Keys Types
interface IApiKey {
    id: string;
    name: string;
    keyPrefix: string;
    scopes: string[];
    status: 'active' | 'revoked' | 'expired';
    lastUsedAt: string | null;
    expiresAt: string | null;
    createdAt: string;
}

interface ICreateApiKeyRequest {
    name: string;
    scopes: string[];
    expiresAt?: string;
    metadata?: any;
}

interface ICreateApiKeyResponse extends IApiKey {
    apiKey: string; // Only returned on creation
}

interface IUpdateApiKeyRequest {
    name?: string;
    expiresAt?: string;
    metadata?: any;
}

// Billing Types
enum SubscriptionPlanEnum {
    Professional = 'professional',
    Enterprise = 'enterprise',
}

enum SubscriptionStatusEnum {
    Trialing = 'trialing',
    Active = 'active',
    PastDue = 'past_due',
    Canceled = 'canceled',
    Unpaid = 'unpaid',
    Incomplete = 'incomplete',
}

interface ISubscription {
    id: number;
    hash: string;
    stripeSubscriptionId: string;
    organisationId: number;
    brandId: number;
    plan: SubscriptionPlanEnum;
    planType?: SubscriptionPlanEnum; // Backend compatibility
    status: SubscriptionStatusEnum;
    billingCycle: 'monthly' | 'yearly';
    trialStart: string | null;
    trialEnd: string | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    stripePriceId: string;
    amount: number;
    currency: string;
    discountCode: string | null;
    createdAt: string;
    updatedAt: string;
}

// Response from subscription creation - includes clientSecret for 3DS authentication
interface ISubscriptionCreateResponse {
    subscription: ISubscription;
    clientSecret?: string; // Present when 3D Secure authentication is required
}

interface ICreateSubscriptionRequest {
    brandId: number;
    plan: SubscriptionPlanEnum;
    billingCycle: 'monthly' | 'yearly';
    paymentMethodId?: string;
    discountCode?: string;
    billingCountry?: string; // ISO 3166-1 alpha-2 country code for tax calculation
}

interface IUpdateSubscriptionRequest {
    plan?: SubscriptionPlanEnum;
    billingCycle?: 'monthly' | 'yearly';
}

interface IPaymentMethod {
    id: number;
    stripePaymentMethodId: string;
    cardBrand: string;
    cardLast4: string;
    cardExpMonth: number;
    cardExpYear: number;
    isDefault: boolean;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface IInvoice {
    id: number;
    stripeInvoiceId: string;
    organisationId: number;
    amount: number;
    currency: string;
    status: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    hostedInvoiceUrl: string;
    invoicePdf: string;
    description: string;
    createdAt: string;
    updatedAt: string;
}

interface IInvoicesResponse {
    data: IInvoice[];
    hasMore: boolean;
    nextCursor: string | null;
}

interface IStripeSetupIntent {
    clientSecret: string;
}

interface IAnnualPromotion {
    active: boolean;
    discountPercent: number;
    couponCode: string | null;
    pricing: {
        monthlyPrice: number;
        yearlyPrice: number;
    };
}

interface ISubscriptionFeatures {
    subscriptionId: number | null;
    hasActiveSubscription: boolean;
    plan: SubscriptionPlanEnum | null;
    status: SubscriptionStatusEnum | null;
    features: {
        benchmarking: boolean;
        aiExpertLevel: 'basic' | 'advanced';
    };
    bypassEnabled?: boolean;
}

// Impersonation Types
interface IImpersonationRequest {
    userId: number;
    reason: string;
    // SYR-96: preview the org unlocked at this plan ('PROFESSIONAL' | 'ENTERPRISE')
    previewPlan?: string;
}

interface IImpersonationResponse {
    access_token: string;
    needsOnboarding: boolean;
    impersonation: {
        originalUserId: number;
        originalUsername: string;
        targetUserId: number;
        targetUsername: string;
        reason: string;
        startedAt: string;
        previewPlan?: string;
    };
    target_user: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
        roles: string[];
        organisationId: number;
        organisationName: string;
    };
}

interface IImpersonationStopResponse {
    message: string;
}

interface IReturnToAdminResponse {
    access_token: string;
}

// Agency types
interface IAgencyContextPayload {
    agencyOrganisationId: number;
    agencyUserId: number;
    agencyUserEmail: string;
    clientOrganisationId: number;
    clientOrganisationName: string;
    accessLevel: 'VIEWER' | 'MANAGER';
    startedAt: string;
    originalTokenExpiry?: number;
}

interface IAgencyContextResponse {
    access_token: string;
    agencyContext: IAgencyContextPayload;
    client_organisation: {
        id: number;
        name: string;
        hash: string;
        templateType?: string;
        status?: string;
        plan?: string;
    };
}

interface IAgencyReturnResponse {
    access_token: string;
}

interface IAgencyClient {
    id: number;
    agencyOrganisationId: number;
    clientOrganisationId: number;
    status: 'ACTIVE' | 'PENDING' | 'SUSPENDED' | 'REMOVED';
    billingResponsibility: 'CLIENT' | 'AGENCY';
    invitedAt: string | null;
    acceptedAt: string | null;
    removedAt: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
    clientOrganisation?: {
        id: number;
        name: string;
        hash: string;
        brands?: Array<{ id: number; name: string; hash: string; status: string }>;
    };
    agencyOrganisation?: {
        id: number;
        name: string;
        hash: string;
    };
}

interface IAgencyClientBrandSummary {
    id: number;
    name: string;
    hash: string;
    status: string;
    latestEquityScore: number | null;
    activeSurveyCount: number;
    totalResponseCount: number;
}

interface IAgencyPortfolioClient {
    clientOrganisationId: number;
    clientOrganisationName: string;
    clientOrganisationHash: string;
    agencyClientId: number;
    billingResponsibility: string;
    notes: string | null;
    brandCount: number;
    activeSurveyCount: number;
    totalResponseCount: number;
    brands: IAgencyClientBrandSummary[];
}

interface IAgencyPortfolioSummary {
    totalClients: number;
    totalBrands: number;
    totalActiveSurveys: number;
    totalResponses: number;
    clients: IAgencyPortfolioClient[];
}

interface IAgencyActivityItem {
    id: number;
    eventType: string;
    clientOrganisationId: number;
    clientOrganisationName: string;
    entityType: string | null;
    entityId: number | null;
    metadata: any;
    createdAt: string;
}

interface IAgencyActivityFeed {
    items: IAgencyActivityItem[];
    total: number;
}

interface IAgencyInviteClientDto {
    clientOrganisationId: number;
    notes?: string;
    grantAccessToUserIds?: number[];
    defaultAccessLevel?: 'VIEWER' | 'MANAGER';
}

interface IAgencyUserAccess {
    id: number;
    userId: number;
    agencyClientId: number;
    accessLevel: 'VIEWER' | 'MANAGER';
    user?: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
    };
    agencyClient?: IAgencyClient;
}

// Agency portfolio analytics (SYR-85)
interface IAgencyBrandBucket {
    businessTypeId: number | null;
    businessTypeSlug: string | null;
    businessTypeName: string | null;
    productTypeId: number | null;
    productTypeSlug: string | null;
    productTypeName: string | null;
}

interface IAgencyElementScore {
    element: string;
    score: number | null;
    classificationLabel?: string;
    locked: boolean;
}

interface IAgencyBenchmarkPosition {
    available: boolean;
    reason?: string;
    vsMean?: number;
    meanValue?: number;
    performanceCategory?: string;
    quartile?: 'top' | 'upper' | 'lower' | 'bottom';
    distribution?: {
        mean: number;
        percentile25: number;
        percentile50: number;
        percentile75: number;
        sampleSize: number;
    };
    elementBenchmarks?: Array<{
        categoryName: string;
        level: number;
        industryMean?: number;
        vsIndustryMean?: number;
        performanceCategory?: string;
        sampleSize?: number;
    }>;
}

interface IAgencySignal {
    type: 'score_drop' | 'gone_quiet' | 'insufficient_data' | 'below_benchmark';
    severity: 'high' | 'medium' | 'info';
    value?: number;
}

interface IAgencyAnalyticsBrand {
    brandId: number;
    brandHash: string;
    brandName: string;
    bucket: IAgencyBrandBucket;
    equity: {
        score: number | null;
        classificationLabel?: string;
        totalResponses: number;
        insufficientSampleSize: boolean;
        previousScore: number | null;
        delta: number | null;
        elements: IAgencyElementScore[];
    };
    benchmark: IAgencyBenchmarkPosition | null;
    signals: IAgencySignal[];
    activeSurveyCount: number;
    responsesInPeriod: number;
    lastResponseAt: string | null;
}

interface IAgencyAnalyticsClient {
    clientOrganisationId: number;
    clientOrganisationName: string;
    clientOrganisationHash: string;
    agencyClientId: number;
    billingResponsibility: string;
    notes: string | null;
    isMock: boolean;
    accessLevel: string;
    brands: IAgencyAnalyticsBrand[];
}

interface IAgencyPortfolioAnalytics {
    period: string;
    calculatedAt: string;
    clients: IAgencyAnalyticsClient[];
}

interface IAgencyBrandTrendPoint {
    month: string;
    calculatedAt: string;
    totalResponses: number;
    overallBrandEquityScore: number;
    latentElementScores: Array<{ element: string; score: number }>;
}

interface IAgencyBrandTrend {
    brandId: string;
    brandName: string;
    organizationId: string;
    timeSeriesData: IAgencyBrandTrendPoint[];
    minResponseThreshold: number;
    lowConfidenceThreshold: number;
}

interface IAgencyClientScorecard {
    client: {
        clientOrganisationId: number;
        clientOrganisationName: string;
        clientOrganisationHash: string;
        agencyClientId: number;
        billingResponsibility: string;
        notes: string | null;
        isMock: boolean;
    };
    brands: Array<{ brandId: number; brandHash: string; brandName: string }>;
    selectedBrandId: number;
    bucket: IAgencyBrandBucket;
    period: string;
    equity: {
        score: number | null;
        classificationLabel?: string;
        totalResponses: number;
        insufficientSampleSize: boolean;
        previousScore: number | null;
        delta: number | null;
        latentElementScores: Array<{
            element: string;
            score: number | null;
            classificationLabel?: string;
            locked: boolean;
            inputs?: Array<{
                name: string;
                type: string;
                value: number | null;
                weight: number;
                contribution: number | null;
            }>;
        }>;
        questionCategoryScores: Array<{
            category: string;
            meanScore: number | null;
            responseCount: number;
            classificationLabel?: string;
            locked: boolean;
        }>;
    };
    benchmark: IAgencyBenchmarkPosition | null;
    signals: IAgencySignal[];
    recentActivity: Array<{
        id: number;
        eventType: string;
        entityType: string | null;
        entityId: number | null;
        metadata: any;
        createdAt: string;
    }>;
}

// AI Experts Types
interface IAIExpert {
    id: string;
    expertType: string;
    name: string;
    specialty: string;
    description: string;
    personaPrompt: string;
    expertise: string[];
    color: string;
    icon: string;
    isActive: boolean;
    searchFilters: Record<string, any>;
    requiredSubscriptionLevel?: SubscriptionPlanEnum;
    knowledgeBaseId?: string;
    llmTemperature: number;
    createdAt: string;
    updatedAt: string;
    hasAccess?: boolean; // Whether user has access based on subscription
}

interface IKnowledgeDocument {
    id: string;
    filename: string;
    fileType: 'pdf' | 'xlsx';
    fileSize: number;
    filePath: string;
    uploadDate: string;
    processedDate?: string;
    processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
    chunkCount: number;
    metadata?: Record<string, any>;
    errorMessage?: string;
    uploader: {
        email: string;
    };
}

interface IDocumentStats {
    totalDocuments: number;
    totalChunks: number;
    processingDocuments: number;
    failedDocuments: number;
    totalSizeBytes: number;
}

interface IAIConversation {
    id: string;
    userId: number;
    organisationId: number;
    expertType: string;
    title?: string;
    lastMessageAt?: string;
    messageCount: number;
    createdAt: string;
    updatedAt: string;
}

interface IAIMessage {
    id: string;
    conversationId: string;
    messageType: 'user' | 'expert';
    content: string;
    sources?: any[];
    metadata?: Record<string, any>;
    tokensUsed?: number;
    createdAt: string;
}

interface IExpertSummary {
    id: string;
    expertType: string;
    name: string;
    specialty: string;
    isActive: boolean;
    color: string;
    icon: string;
    conversationCount: number;
    avgResponseTime: number;
    satisfaction: number;
}

interface ISystemStats {
    totalExperts: number;
    activeExperts: number;
    totalDocuments: number;
    totalChunks: number;
    totalConversations: number;
    avgSatisfaction: number;
    storageUsed: number;
    processingQueue: number;
}

interface IUpdateExpertRequest {
    name?: string;
    specialty?: string;
    description?: string;
    personaPrompt?: string;
    expertise?: string[];
    color?: string;
    icon?: string;
    isActive?: boolean;
    searchFilters?: Record<string, any>;
    knowledgeBaseId?: string;
    llmTemperature?: number;
}

interface IChatRequest {
    expertType: string;
    message: string;
    conversationId?: string;
    brandId?: number;
}

interface IChatResponse {
    id: string;
    content: string;
    sources: any[];
    conversationId: string;
    tokensUsed: number;
    responseTime: number;
}


// AI Token Usage Types
interface ITokenUsageDaily {
    date: string;
    tokens: number;
    requests: number;
}

interface ITokenUsageFeature {
    featureType: string;
    tokens: number;
    requests: number;
    percentage: number;
}

interface IOrganizationTokenUsage {
    // Null for platform-initiated AI spend that no customer owns (SYR-107);
    // `source` names the bucket in that case.
    organisationId: number | null;
    organisationName: string;
    source?: string | null;
    totalTokens: number;
    totalRequests: number;
}

interface ITokenUsageSummary {
    dailySummary: ITokenUsageDaily[];
    currentUsage: {
        withinLimits: boolean;
        currentMonthly: number;
        currentDaily: number;
    };
    featureBreakdown: ITokenUsageFeature[];
    totalTokensThisMonth: number;
    totalTokensToday: number;
    topOrganizations?: IOrganizationTokenUsage[];
    estimatedCost?: {
        daily: number;
        monthly: number;
        currency: string;
    };
    enforcementEnabled?: boolean;
}

interface IUserTokenUsage {
    userId: number;
    userName: string;
    userEmail: string;
    userRoles: string[];
    tokens: number;
    requests: number;
    lastActivity: Date;
    featureBreakdown: Array<{
        featureType: string;
        tokens: number;
        requests: number;
    }>;
}

interface IOrganizationTokenUsageByUser {
    totalTokens: number;
    totalRequests: number;
    totalCost: number;
    userBreakdown: IUserTokenUsage[];
    dailySummary: ITokenUsageDaily[];
}

// Brand Analytics interfaces
interface IBrandAnalyticsStatementScore {
    id: string;
    text: string;
    scoringType: string;
    questionTypeId: number;
    responseCount: number;
    meanScore: number | null;
    polarityValue: number | null;
    polarityPercentage: number | null;
    intensityValue: number | null;
    intensityPercentage: number | null;
    brandPowerPercentage: number | null;
    controversyValue: number | null;
    controversyPercentage: number | null;
    scoreDistribution: Record<string, number> | null;
}

interface IBrandAnalyticsMeasuredElementScore {
    category: string;
    meanScore: number | null;
    responseCount: number;
    standardDeviation: number | null;
    meanPolarityPercentage: number | null;
    meanIntensityPercentage: number | null;
    meanBrandPowerPercentage: number | null;
    meanControversyPercentage: number | null;
    input: IBrandAnalyticsStatementScore[];
    aiSummary?: string;
}

interface IBrandAnalyticsLatentElementInput {
    name: string;
    type: 'QC' | 'LE'; // QC = Measured Element, LE = Latent Element
    value: number;
}

interface IBrandAnalyticsLatentElementScore {
    element: string;
    score: number;
    inputs: IBrandAnalyticsLatentElementInput[];
    aiSummary?: string;
}

// SYR-111 — the customer read. The API answers 200 with a reason when gated,
// so `entitled` and `enabled` are states the page renders, not errors.
interface IModelBeliefsRead {
    entitled: boolean;
    enabled: boolean;
    // True for a platform admin or an impersonation session while the master
    // switch is off: the page works, and says it is not live for customers yet.
    previewOnly?: boolean;
    // Set when ?run= on the page replaced the latest customer read (admins
    // and impersonation only), so the page can say which run it is showing.
    pinnedRunId?: number | null;
    // True when ?survey=0 dropped the customer line (admins and impersonation).
    surveyHidden?: boolean;
    // True for a platform admin or an impersonation session: the page shows
    // the re-synthesis controls.
    canResynthesise?: boolean;
    // False hides Read again: reads happen on the schedule only.
    manualReadEnabled?: boolean;
    config?: {
        consumerName: string | null;
        categoryLabel: string | null;
        marketCountry: string | null;
        competitorNames: string[] | null;
    } | null;
    latest?: {
        runId: number;
        completedAt: string | null;
        market: string | null;
        marketName?: string | null;
        panel: { label: string; provider: string; model: string }[];
        sampling: Record<string, unknown>;
        consensus: Record<string, unknown> | null;
        byModel: unknown[];
        panelCoverage?: unknown;
        gate: IModelBeliefsGateSummary | null;
        // The v3 page: statements with their group, the panel consensus per
        // target and mode, the per-model spread and the survey line when one
        // exists. notAsked is Group C, which only the survey can answer.
        statements?: IModelBeliefsStatementRead[];
        notAsked?: { code: string; category: string; group: string | null; text: string }[];
        groupsAsked?: string[] | null;
        consumerName?: string | null;
        // v4: 'pages' when the searched view is what the pages the models read
        // say, scored page by page; competitors are then compared from memory.
        searchedSource?: 'pages' | 'search';
        pagesRead?: number;
        pageMoves?: IModelBeliefsPageMoves[];
        pageFindings?: IModelBeliefsPageFindings | null;
        // v4: every found page reading, keyed by statement code.
        pageReadings?: Record<string, IModelBeliefsPageReading[]>;
        sources: { ledger: unknown; impostorCandidates: unknown } | null;
        movement: unknown;
        synthesis: IModelBeliefsSynthesis | null;
        recordFixes?: IModelBeliefsRecordFix[];
        degraded?: boolean;
    } | null;
    history?: {
        date: string;
        aiBrandEquity: number | null;
        gateInclusionRate: number | null;
        models: number;
        panelSize: number;
    }[];
    inFlight?: { status: string; stage: string | null; callsMade: number; callsBudget: number } | null;
    canRequest?: boolean;
    nextRequestAt?: string | null;
}

interface IModelBeliefsGateSummary {
    // How the question was asked, in the words a reader with no knowledge of
    // the method needs: the question itself, and how many answers it got.
    method?: {
        question: string;
        models: string[];
        perModelSearched: number;
        perModelMemory: number;
        searchedAnswers: number;
        memoryAnswers: number;
        totalAnswers: number;
    } | null;
    byMode?: {
        retrievalMode: 'on' | 'off';
        // Answers the question got in this mode (replicates x models).
        asked?: number;
        included?: number;
        inclusionRate: number; // fraction 0..1
        meanPosition: number | null;
        // Rank question (v3): share named first, and the mean 7..1 score over
        // every call with absent counted as 1.
        namedFirstRate?: number;
        rankScore?: number | null;
        modelsIncluding?: number;
    }[];
    byCell?: {
        label?: string;
        provider?: string;
        model?: string;
        retrievalMode?: 'on' | 'off';
        inclusionRate: number;
        meanPosition: number | null;
        namedFirstRate?: number;
        rankScore?: number | null;
    }[];
    competitors?: {
        name: string;
        inclusionRate: number;
        meanPosition: number | null;
        namedFirstRate?: number;
        rankScore?: number | null;
        reasons?: string[];
    }[];
    // Brands the models named that nobody configured, with the same numbers
    // as a competitor on the searched answers (absent on older reads).
    unknownBrands?: {
        name: string;
        count: number;
        inclusionRate?: number;
        meanPosition?: number | null;
        namedFirstRate?: number;
        rankScore?: number | null;
    }[];
    // The rank table as the API ranks it: the brand, its configured
    // competitors, and any unconfigured brand the models put ahead of it.
    rankTable?: {
        name: string;
        isBrand: boolean;
        configured: boolean;
        inclusionRate: number;
        meanPosition: number | null;
        namedFirstRate: number;
        rankScore: number | null;
    }[];
}

// One aggregate cell: the panel consensus (or one model) for a statement under
// one retrieval mode, both views.
interface IModelBeliefsCell {
    self: number | null;
    customer: number | null;
    n: number;
    sd: number | null;
    ciLow: number | null;
    ciHigh: number | null;
    noBeliefRate: number | null;
    naRate: number | null;
    // Rates for the customer view; absent on reads served before they existed.
    customerNoBeliefRate?: number | null;
    customerNaRate?: number | null;
}

interface IModelBeliefsStatementRead {
    code: string;
    text: string;
    category: string;
    group: 'A' | 'B' | 'C' | null;
    selfFromRank: boolean;
    scaleMin: number;
    scaleMax: number;
    allowsNa: boolean;
    higherIsBetter: boolean;
    // The best brand on the line by the written read's rule (null on a tie),
    // and whether the models agree with each other on the brand's score.
    best?: string | null;
    agree?: 'yes' | 'mostly' | 'no' | 'unknown';
    brand: { searched: IModelBeliefsCell | null; memory: IModelBeliefsCell | null; pages?: IModelBeliefsCell | null };
    competitors: {
        name: string;
        searched: IModelBeliefsCell | null;
        memory: IModelBeliefsCell | null;
        byModel: { label: string; searched: IModelBeliefsCell | null; memory?: IModelBeliefsCell | null }[];
    }[];
    byModel: { label: string; searched: IModelBeliefsCell | null; memory: IModelBeliefsCell | null; pages?: IModelBeliefsCell | null }[];
    survey: { mean: number; n: number } | null;
    // v4: the reasons the models gave for their answers, per assistant.
    reasons?: { label: string; text: string }[];
}

// v4: what one page read says on one statement.
interface IModelBeliefsPageReading {
    domain: string;
    url: string;
    answers: number;
    score: number;
    quote: string;
}

// v4: a page whose presence in an answer goes with a clearly higher or lower
// rank for a brand, within one model. Association, not cause.
interface IModelBeliefsPageMove {
    domain: string;
    kind: string;
    // The brand whose own site this is (a competitor or another brand the
    // models named); null or absent when it is nobody's.
    site?: string | null;
    nPresent: number;
    nAbsent: number;
    scorePresent: number;
    scoreAbsent: number;
    listedPresent: number;
    listedAbsent: number;
}

interface IModelBeliefsPageMoves {
    provider: string;
    model: string;
    label: string;
    answers: number;
    targets: {
        name: string;
        isBrand: boolean;
        helps: IModelBeliefsPageMove[];
        hurts: IModelBeliefsPageMove[];
        // The same by kind of page (list articles, review sites, the brand's own site).
        kindHelps?: IModelBeliefsPageMove[];
        kindHurts?: IModelBeliefsPageMove[];
    }[];
}

interface IModelBeliefsPageFinding {
    domain: string;
    url: string;
    // The page's own title, so two pages of one site can be told apart.
    title: string | null;
    kind: string;
    answers: number;
    models: { label: string; answers: number }[];
    covered: number;
    asked: number;
    // Whether the page names the brand at all; null when not counted.
    named: boolean | null;
    // The brand whose own site this is (the brand, a competitor or another
    // brand the models named); null when it is nobody's.
    site: string | null;
    // The market the page belongs to when it is not the read's (ISO country);
    // null in the market. Absent on reads served before it existed.
    market?: string | null;
    // Whether the page is a place the brand is missing from, decided by the
    // API (never names the brand, nobody's own site, not a reference page,
    // in the market). Absent on older reads.
    gap?: boolean;
    // Silent pages are folded to one per domain; how many pages this stands for.
    pages: number;
    readings: { code: string; score: number; quote: string }[];
}

interface IModelBeliefsPageFindings {
    pagesRead: number;
    speaking: IModelBeliefsPageFinding[];
    silent: IModelBeliefsPageFinding[];
    unreadable: { domain: string; url: string; answers: number }[];
}

interface IModelBeliefsSynthesis {
    headline?: string;
    summary?: string;
    gate_note?: string;
    // v6 (v3 instrument): a paragraph under the facts table and the opinions table.
    facts_note?: string | null;
    beliefs_note?: string | null;
    // v4: the pages that move the rank and what the most-read pages say.
    pages_note?: string | null;
    competitor_note?: string;
    since_last_read?: string;
    elements?: unknown[];
}

interface IModelBeliefsRecordFix {
    card: {
        type: 'record';
        source: 'model_beliefs';
        element?: string;
        title?: string;
        rationale?: string;
        objectives?: { task: string; detail?: string; impact?: string; effort?: string; moves?: string }[];
        citations?: string[];
        level?: string;
    };
    playId: string;
    added: boolean;
    done: number[];
}

interface IBrandAnalyticsMonthlyData {
    month: string; // "2025-03"
    calculatedAt: string;
    totalResponses: number;
    overallBrandEquityScore: number; // -100 to +100 scale
    latentElementScores: IBrandAnalyticsLatentElementScore[];
    questionCategoryScores: IBrandAnalyticsMeasuredElementScore[];
}

interface IBrandAnalytics {
    brandId: string;
    brandName: string;
    organizationId: string;
    industry: string;
    timeSeriesData: IBrandAnalyticsMonthlyData[];
    minResponseThreshold: number; // Minimum responses required (currently 10)
    lowConfidenceThreshold: number; // Below this count shows "low confidence" warning (default 50)
}

interface IBrandAnalyticsSummary {
    id: string;
    name: string;
    organizationId: string;
    industry: string;
    productType?: string;
    hash: string;
    isMock?: boolean;
}

interface IBrandAnalyticsMetricOption {
    key: string;
    label: string;
    type: 'brandEquity' | 'latentElement';
    drillDownAvailable: boolean;
}

// Dashboard AI interfaces
interface IDashboardAIConfig {
    id: number;
    expertType: string;
    dashboardPrompt: string;
    systemPrompt?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface IUpdateDashboardConfigRequest {
    dashboardPrompt: string;
    systemPrompt?: string;
}

interface IDashboardInsightRequest {
    expertType: string;
    forceRefresh?: boolean;
}

interface IDashboardInsightResponse {
    response: string;
    cached: boolean;
}

interface IDashboardInsightsResponse {
    [expertType: string]: IDashboardInsightResponse;
}

// Advice AI interfaces
interface IAdviceAIConfig {
    id: number;
    expertType: string;
    advicePrompt: string;
    systemPrompt?: string;
    targetWordCount: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface IUpdateAdviceConfigRequest {
    advicePrompt: string;
    systemPrompt?: string;
    targetWordCount?: number;
}

interface IAdviceInsightResponse {
    response: string;
    cached: boolean;
}

interface IAdviceInsightsResponse {
    [expertType: string]: IAdviceInsightResponse;
}

// Orbital AI interfaces
interface IOrbitalAIConfig {
    id: number;
    expertType: string;
    systemPrompt: string;
    userPrompt: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface IUpdateOrbitalConfigRequest {
    systemPrompt: string;
    userPrompt: string;
}

interface IOrbitalItemResult {
    items: string[] | null;
    reason?: string;
    cached?: boolean;
}

interface IOrbitalItemsResponse {
    enabled: boolean;
    orbital_findings: IOrbitalItemResult;
    orbital_actions: IOrbitalItemResult;
}

// Building Blocks AI interfaces
interface IBuildingBlocksAIConfig {
    id: number;
    configKey: string;
    systemPrompt: string;
    userPromptTemplate: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface IUpdateBuildingBlocksConfigRequest {
    systemPrompt: string;
    userPromptTemplate: string;
}

interface IBuildingBlocksInsightResult {
    response: string | null;
    cached: boolean;
    elementKey: string;
}

interface IBuildingBlocksInsightsResponse {
    insights: Record<string, IBuildingBlocksInsightResult>;
    mode: 'combined' | 'individual';
}

// Messaging interfaces
interface IMessageType {
    id: number;
    code: string;
    name: string;
    description: string;
    color: string;
    isSystem?: boolean;
    displayOrder: number;
}

interface IMessage {
    id: number;
    hash: string;
    subject: string;
    bodyHtml: string;
    bodyText: string;
    preview: string;
    priority: string;
    status: string;
    scheduledAt?: string;
    sentAt?: string;
    createdAt: string;
    messageType: IMessageType;
    senderUser?: {
        firstName: string;
        lastName: string;
    };
    recipientCount: number;
    emailsSent: number;
    readCount: number;
    recipientStatus?: {
        isRead: boolean;
        isStarred: boolean;
        isArchived: boolean;
        isDeleted: boolean;
        readAt?: string;
        starredAt?: string;
        archivedAt?: string;
        deletedAt?: string;
    };
}

interface IMessageRecipient {
    id: number;
    message: IMessage;
    isRead: boolean;
    isStarred: boolean;
    isArchived: boolean;
    isDeleted: boolean;
    createdAt: string;
}

interface IMessagePreference {
    messageType: IMessageType;
    emailEnabled: boolean;
    inAppEnabled: boolean;
    unsubscribeToken?: string;
}

interface IInboxStats {
    total: number;
    unread: number;
    starred: number;
    archived: number;
}

interface IAdminStats {
    overview: {
        totalMessages: number;
        draftMessages: number;
        scheduledMessages: number;
        sentMessages: number;
        totalRecipients: number;
        emailsSent: number;
        messagesRead: number;
        readRate: number;
        deliveryRate: number;
    };
    messageTypeStats: Array<{
        name: string;
        code: string;
        count: number;
    }>;
    recentActivity: Array<{
        id: number;
        hash: string;
        subject: string;
        status: string;
        createdAt: string;
        messageType: {
            name: string;
            code: string;
        };
        senderUser?: {
            firstName: string;
            lastName: string;
        };
    }>;
}

interface ICreateMessageDto {
    messageTypeCode: string;
    subject: string;
    bodyHtml: string;
    bodyText?: string;
    preview?: string;
    priority?: string;
    scheduledAt?: string;
    allUsers?: boolean;
    allOrganisationUsers?: boolean;
    recipientUserIds?: number[];
    recipientOrganisationIds?: number[];
    metadata?: any;
}

interface IUpdateMessageStatusDto {
    isRead?: boolean;
    isStarred?: boolean;
    isArchived?: boolean;
    isDeleted?: boolean;
}

interface IBulkUpdateMessageStatusDto {
    messageIds: number[];
    status: IUpdateMessageStatusDto;
}

interface IBulkUpdatePreferencesDto {
    preferences: Array<{
        messageTypeCode: string;
        emailEnabled: boolean;
        inAppEnabled: boolean;
    }>;
}

interface IAIQuotaStatus {
    withinQuota: boolean;
    daily: {
        usage: number;
        limit: number;
        remaining: number;
        percentageUsed: number;
    };
    monthly: {
        usage: number;
        limit: number;
        remaining: number;
        percentageUsed: number;
    };
    alertTriggered: boolean;
    warningTriggered: boolean;
    limitType: 'daily' | 'monthly' | null;
    enforcementEnabled: boolean;
}

// Custom Brand Chart interfaces
interface ICustomBrandChart {
    id: number;
    userId: number;
    brandId: number;
    chartName: string;
    selectedMetrics: string[];
    displayOrder: number;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface ICreateCustomBrandChartRequest {
    chartName: string;
    selectedMetrics: string[];
    brandHash: string;
}

interface IUpdateCustomBrandChartRequest {
    chartName?: string;
    selectedMetrics?: string[];
}

interface IReorderCustomBrandChartsRequest {
    chartIds: number[];
    brandHash: string;
}

// ============================================
// Support Ticketing Interfaces
// ============================================

interface ITicket {
    id: number;
    hash: string;
    subject: string;
    description: string;
    status: 'open' | 'in_progress' | 'waiting_on_customer' | 'resolved' | 'closed';
    priority: 'low' | 'normal' | 'high' | 'urgent';
    source: 'web' | 'email' | 'api';
    organisationId: number;
    brandId: number | null;
    createdByUserId: number;
    assignedToUserId: number | null;
    categoryId: number | null;
    ipAddress: string | null;
    userAgent: string | null;
    metadata: any;
    firstResponseAt: string | null;
    resolvedAt: string | null;
    closedAt: string | null;
    createdAt: string;
    updatedAt: string;
    createdByUser: ITicketUser;
    assignedToUser?: ITicketUser;
    category?: ITicketCategory;
    brand?: ITicketBrand;
    organisation?: ITicketOrganisation;
    comments?: ITicketComment[];
    attachments?: ITicketAttachment[];
    satisfactionRating?: ITicketSatisfactionRating;
}

interface ITicketUser {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
}

interface ITicketComment {
    id: number;
    ticketId: number;
    content: string;
    isInternal: boolean;
    authorUserId: number;
    createdAt: string;
    updatedAt: string;
    authorUser: ITicketUser;
}

interface ITicketAttachment {
    id: number;
    hash: string;
    ticketId: number;
    commentId: number | null;
    filename: string;
    originalFilename: string;
    filePath: string;
    mimeType: string;
    fileSize: number;
    uploadedByUserId: number;
    createdAt: string;
    uploadedByUser: ITicketUser;
}

interface ITicketCategory {
    id: number;
    organisationId: number | null;
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    autoAssignToUserId: number | null;
    slaResponseHours: number | null;
    isActive: boolean;
    displayOrder: number;
    createdAt: string;
    updatedAt: string;
}

interface ITicketBrand {
    id: number;
    name: string;
}

interface ITicketOrganisation {
    id: number;
    name: string;
}

interface ITicketSatisfactionRating {
    id: number;
    ticketId: number;
    rating: number;
    feedback: string | null;
    createdAt: string;
}

interface ICannedResponse {
    id: number;
    organisationId: number | null;
    title: string;
    content: string;
    categoryId: number | null;
    isActive: boolean;
    usageCount: number;
    createdAt: string;
    updatedAt: string;
    category?: ITicketCategory;
}

interface ICreateTicketDto {
    subject: string;
    description: string;
    categoryId?: number;
    brandId?: number;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
}

interface IUpdateTicketDto {
    subject?: string;
    description?: string;
    status?: 'open' | 'in_progress' | 'waiting_on_customer' | 'resolved' | 'closed';
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    categoryId?: number;
    assignedToUserId?: number;
    statusChangeReason?: string;
}

interface ICreateCommentDto {
    content: string;
    isInternal?: boolean;
}

interface ITicketQueryDto {
    status?: string;
    priority?: string;
    categoryId?: number;
    assignedToUserId?: number;
    createdByUserId?: number;
    search?: string;
    sortBy?: 'created' | 'updated' | 'priority';
    sortOrder?: 'ASC' | 'DESC';
    limit?: number;
    offset?: number;
}

interface ITicketListResponse {
    tickets: ITicket[];
    total: number;
}

interface ITicketStatsResponse {
    byStatus: Array<{ status: string; count: number }>;
    byPriority: Array<{ priority: string; count: number }>;
    avgResponseTimeSeconds: number;
    avgResolutionTimeSeconds: number;
}

// Legal Documents
enum LegalDocumentType {
    PRIVACY_POLICY = 'privacy_policy',
    TERMS_OF_SERVICE = 'terms_of_service',
}

enum AcceptanceMethod {
    SIGNUP = 'signup',
    LOGIN_PROMPT = 'login_prompt',
    SETTINGS = 'settings',
    API = 'api',
}

interface ILegalDocument {
    id: number;
    documentType: LegalDocumentType;
    version: string;
    title: string;
    content: string;
    summary?: string;
    isCurrent: boolean;
    effectiveDate: string;
    createdAt: string;
}

interface ILegalAcceptance {
    id: number;
    documentId: number;
    acceptedAt: string;
    acceptanceMethod: AcceptanceMethod;
    document?: ILegalDocument;
}

interface ILegalAcceptanceStatus {
    enabled: boolean;
    allAccepted: boolean;
    pendingDocuments: LegalDocumentType[];
    acceptedDocuments: {
        documentType: LegalDocumentType;
        version: string;
        acceptedAt: string;
    }[];
}

interface IAcceptDocumentRequest {
    documentType: LegalDocumentType;
    acceptanceMethod?: AcceptanceMethod;
}

interface IAcceptMultipleDocumentsRequest {
    documentTypes: LegalDocumentType[];
    acceptanceMethod?: AcceptanceMethod;
}

// Final Questions interfaces
interface IFinalQuestion {
    id: number;
    text: string;
    displayOrder: number;
    isActive: boolean;
    useAsDefault: boolean;
    usageCount?: number;
    createdAt: string;
    updatedAt: string;
}

interface IFinalQuestionsListResponse {
    data: IFinalQuestion[];
    total: number;
    page: number;
    limit: number;
    surveysWithoutFinalQuestion?: number;
}

interface ICreateFinalQuestionDto {
    text: string;
    displayOrder?: number;
    isActive?: boolean;
    useAsDefault?: boolean;
}

interface IUpdateFinalQuestionDto {
    text?: string;
    displayOrder?: number;
    isActive?: boolean;
    useAsDefault?: boolean;
}

interface IBulkApplyFinalQuestionResponse {
    success: boolean;
    updatedCount: number;
    message: string;
}

interface IActiveFinalQuestion {
    id: number;
    text: string;
    useAsDefault: boolean;
}

interface IActiveFinalQuestionsResponse {
    data: IActiveFinalQuestion[];
    defaultFinalQuestionId?: number;
}

interface IFinalResponse {
    id: number;
    participantIdentifier: string;
    participantEmail?: string;
    questionText: string;
    responseText: string;
    createdAt: string;
}

interface IFinalResponsesListResponse {
    data: IFinalResponse[];
    total: number;
    page: number;
    limit: number;
}

export {
    EntityTag,
    SubscriptionPlanEnum,
    SubscriptionStatusEnum,
    PeriodEnum,
    LegalDocumentType,
    AcceptanceMethod,
};

export type {
    IAuth,
    IApiUser,
    IUpdateApiUser,
    ICreateApiUser,
    ICreateOrganisationWithApiUser,
    ICreateWaitlistEntry,
    IWaitlistEntry,
    IWaitlistStats,
    ICharterPageConfig,
    ISubmitCharterApplication,
    ICharterApplication,
    IHypothesisLead,
    IHypothesisDomainPolicy,
    IHypothesisLeadPage,
    IHypothesisEvidence,
    IHypothesisSubjectDetail,
    IPublicHypothesisConfig,
    IPublicHypothesisRequestResult,
    IPublicHypothesisVerifyResult,
    IPublicHypothesisStatus,
    IPublicHypothesisResult,
    IPublicHypothesisElementReading,
    ICharterApplicationStats,
    CharterCustomerCountBand,
    CharterReachableListBand,
    CharterSendWithin30Days,
    CharterApplicationStatus,
    IOrganisation,
    IFeatureSpecification,
    IFeature,
    IOrganisationFeatures,
    IBrand,
    IBrandEquity,
    IDimensionData,
    ITrendData,
    IQuestionTemplate,
    IUpdateQuestionTemplate,
    ISurvey,
    ISurveySettings,
    ISurveyTheme,
    ICreateSurveyPayload,
    ISurveyCohort,
    IAdminSurveyCohortResponse,
    ISurveyResponse,
    IQuestionResponse,
    IQuestion,
    ISurveyResponseSummary,
    ICoverageStatement,
    ICoverageCategory,
    ICoverageSummary,
    IQuestionCoverageResponse,
    ICalculateBrandEquityRequest,
    IQuestionCategoryScore,
    ILatentElementInput,
    ILatentElementScore,
    IBrandEquityFilters,
    IBrandEquityScoreResponse,
    IAvailableSurvey,
    IApiKey,
    ICreateApiKeyRequest,
    ICreateApiKeyResponse,
    IUpdateApiKeyRequest,
    ISubscription,
    ISubscriptionCreateResponse,
    ICreateSubscriptionRequest,
    IUpdateSubscriptionRequest,
    IPaymentMethod,
    IInvoice,
    IInvoicesResponse,
    IStripeSetupIntent,
    IAnnualPromotion,
    ISubscriptionFeatures,
    IImpersonationRequest,
    IImpersonationResponse,
    IImpersonationStopResponse,
    IReturnToAdminResponse,
    IAgencyContextPayload,
    IAgencyContextResponse,
    IAgencyReturnResponse,
    IAgencyClient,
    IAgencyClientBrandSummary,
    IAgencyPortfolioClient,
    IAgencyPortfolioSummary,
    IAgencyActivityItem,
    IAgencyActivityFeed,
    IAgencyInviteClientDto,
    IAgencyUserAccess,
    IAgencyBrandBucket,
    IAgencyElementScore,
    IAgencyBenchmarkPosition,
    IAgencySignal,
    IAgencyAnalyticsBrand,
    IAgencyAnalyticsClient,
    IAgencyPortfolioAnalytics,
    IAgencyBrandTrend,
    IAgencyBrandTrendPoint,
    IAgencyClientScorecard,
    // Note: IAgencyPortfolioClient and IAgencyClientBrandSummary are exported above
    IAIExpert,
    IKnowledgeDocument,
    IDocumentStats,
    IAIConversation,
    IAIMessage,
    IExpertSummary,
    ISystemStats,
    IUpdateExpertRequest,
    IChatRequest,
    IChatResponse,
    IAdminBrandResponse,
    IAdminSurveyResponse,
    ITokenUsageSummary,
    ITokenUsageDaily,
    ITokenUsageFeature,
    IOrganizationTokenUsage,
    IUserTokenUsage,
    IOrganizationTokenUsageByUser,
    IBrandAnalyticsStatementScore,
    IBrandAnalyticsMeasuredElementScore,
    IBrandAnalyticsLatentElementInput,
    IBrandAnalyticsLatentElementScore,
    IBrandAnalyticsMonthlyData,
    IModelBeliefsRead,
    IModelBeliefsGateSummary,
    IModelBeliefsCell,
    IModelBeliefsStatementRead,
    IModelBeliefsPageMove,
    IModelBeliefsPageMoves,
    IModelBeliefsPageFinding,
    IModelBeliefsPageFindings,
    IModelBeliefsPageReading,
    IModelBeliefsSynthesis,
    IModelBeliefsRecordFix,
    IBrandAnalytics,
    IBrandAnalyticsSummary,
    IBrandAnalyticsMetricOption,
    IDashboardAIConfig,
    IUpdateDashboardConfigRequest,
    IDashboardInsightRequest,
    IDashboardInsightResponse,
    IDashboardInsightsResponse,
    IAdviceAIConfig,
    IUpdateAdviceConfigRequest,
    IAdviceInsightResponse,
    IAdviceInsightsResponse,
    IOrbitalAIConfig,
    IUpdateOrbitalConfigRequest,
    IOrbitalItemResult,
    IOrbitalItemsResponse,
    IBuildingBlocksAIConfig,
    IUpdateBuildingBlocksConfigRequest,
    IBuildingBlocksInsightResult,
    IBuildingBlocksInsightsResponse,
    IMessage,
    IMessageType,
    IMessageRecipient,
    IMessagePreference,
    IInboxStats,
    IAdminStats,
    ICreateMessageDto,
    IUpdateMessageStatusDto,
    IBulkUpdateMessageStatusDto,
    IBulkUpdatePreferencesDto,
    IAIQuotaStatus,
    ICustomBrandChart,
    ICreateCustomBrandChartRequest,
    IUpdateCustomBrandChartRequest,
    IReorderCustomBrandChartsRequest,
    // Support Tickets
    ITicket,
    ITicketUser,
    ITicketComment,
    ITicketAttachment,
    ITicketCategory,
    ITicketBrand,
    ITicketOrganisation,
    ITicketSatisfactionRating,
    ICannedResponse,
    ICreateTicketDto,
    IUpdateTicketDto,
    ICreateCommentDto,
    ITicketQueryDto,
    ITicketListResponse,
    ITicketStatsResponse,
    // Legal Documents
    ILegalDocument,
    ILegalAcceptance,
    ILegalAcceptanceStatus,
    IAcceptDocumentRequest,
    IAcceptMultipleDocumentsRequest,
    // Final Questions
    IFinalQuestion,
    IFinalQuestionsListResponse,
    ICreateFinalQuestionDto,
    IUpdateFinalQuestionDto,
    IBulkApplyFinalQuestionResponse,
    IActiveFinalQuestion,
    IActiveFinalQuestionsResponse,
    IFinalResponse,
    IFinalResponsesListResponse,
    // Subscriptions
    ISubscriptionPlan,
    ICreateSubscriptionPlanDto,
    IUpdateSubscriptionPlanDto,
    IFeatureDefinition,
    ICreateFeatureDto,
    IUpdateFeatureDto,
    IPlanEntitlement,
    ICreatePlanEntitlementDto,
    ValueType,
    IAddonEntitlement,
    ICreateAddonEntitlementDto,
    IOrganisationSubscription,
    IFreeTrialStatus,
    IEntitlementCheckResponse,
    IRestrictedAllowedElements,
    IBenchmarkingAccessResult,
    ITierAccessSummary,
    IUsageRecordDto,
    ICreditBalanceResponse,
    IConsumeCreditsDto,
    IFoundingCohortMember,
    IUpdateFoundingCohortStatusDto,
    IEligibleOrganisation,
    IBulkAddFoundingCohortResult,
    IRewardEarning,
    IRedeemRewardDto,
    IRewardTier,
    ICreateRewardTierDto,
    IUpdateRewardTierDto,
    IGrantRewardDto,
    IRewardRedemption,
    IRewardStats,
    RewardType,
    RewardStatus,
    IStripeSyncResult,
    IStripeBulkSyncResult,
    IStripeRetryResult,
    AddonType,
    IAddon,
    ICreateAddonDto,
    IUpdateAddonDto,
    // Organisation Addons (purchased)
    AddonStatus,
    IOrganisationAddon,
    IAvailableRewardCredit,
    IAddonPurchasePreview,
    ICheckoutResult,
    // On-platform checkout with Payment Element
    IPaymentIntentResult,
    IAddonSubscriptionResult,
    IAddonPaymentMethod,
    // Customer Segmentation
    SegmentationFieldType,
    SegmentationFieldSource,
    ISegmentationFieldMetadata,
    ISegmentationField,
    ICreateSegmentationFieldDto,
    IUpdateSegmentationFieldDto,
    ISegmentationValue,
    ICustomer,
    ICreateCustomerDto,
    IUpdateCustomerDto,
    ICustomerListResponse,
    ISegmentationFieldsLimit,
    // Customer Import / Export
    ImportFileType,
    ImportState,
    ImportMatchStrategy,
    ImportConflictPolicy,
    ImportCoreSlot,
    ColumnMappingTarget,
    ImportColumnMapping,
    IImportErrorSample,
    ICustomerImportJob,
    ICustomerImportRow,
    ICustomerImportPreview,
    ISetImportMappingDto,
    // Saved Segments + Cohort Compare
    SegmentOperator,
    MatchLogic,
    ISegmentFilterCriterion,
    ISegmentSurveyCriteria,
    ISavedSegment,
    ICreateSavedSegmentDto,
    IUpdateSavedSegmentDto,
    ISegmentLibraryUsage,
    ISegmentCriteriaOptions,
    ISegmentComparisonSpec,
    ISegmentComparisonDateRange,
    ISegmentComparisonPeriod,
    ISegmentComparisonRequest,
    ISegmentComparisonBucket,
    ISegmentComparisonSeries,
    ISegmentComparisonResponse,
    IEvaluateSegmentResponse,
    ICohortDefinition,
    ICohortCompareRequest,
    ICohortDeltas,
    SampleSizeWarningCode,
    ISampleSizeWarning,
    ICohortCompareResult,
    // Customer Integrations
    IntegrationType,
    IntegrationStatus,
    IntegrationCoreSlot,
    IntegrationColumnMappingTarget,
    IntegrationColumnMapping,
    IIntegrationConfig,
    ICustomerIntegration,
    ICreateIntegrationDto,
    IUpdateIntegrationDto,
    IRotateSecretResponse,
    SignupFunnelStageKey,
    ISignupFunnelStage,
    ISignupFunnelOrg,
    ISignupFunnelResponse,
}

// ===== Subscription Types =====
interface ISubscriptionPlan {
    id: string;
    code: string;
    name: string;
    description: string;
    billingPeriod: 'monthly' | 'annual' | 'quarterly';
    priceCents: number;
    currency: string;
    stripePriceId?: string;
    stripeProductId?: string;
    stripeSyncStatus?: 'pending' | 'synced' | 'failed';
    stripeSyncError?: string;
    stripeSyncedAt?: string;
    isPublic: boolean;
    isActive: boolean;
    displayOrder: number;
    highlightLabel?: string;
    metadata?: any;
    createdAt: string;
    updatedAt: string;
}

interface IStripeSyncResult {
    success: boolean;
    stripeProductId?: string;
    stripePriceId?: string;
    error?: string;
}

interface IStripeBulkSyncResult {
    synced: number;
    failed: number;
}

interface IStripeRetryResult {
    plans: number;
    addons: number;
}

// Addon Types
type AddonType = 'one_time' | 'recurring' | 'usage_pack';

interface IAddon {
    id: string;
    code: string;
    name: string;
    description?: string;
    addonType: AddonType;
    billingPeriod?: 'monthly' | 'annual' | 'quarterly';
    priceCents: number;
    currency: string;
    stripePriceId?: string;
    stripeProductId?: string;
    stripeSyncStatus?: 'pending' | 'synced' | 'failed';
    stripeSyncError?: string;
    stripeSyncedAt?: string;
    isPublic: boolean;
    isActive: boolean;
    compatiblePlans?: string[];
    metadata?: any;
    createdAt: string;
    updatedAt: string;
}

interface ICreateAddonDto {
    code: string;
    name: string;
    description?: string;
    addonType: AddonType;
    billingPeriod?: 'monthly' | 'annual' | 'quarterly';
    priceCents: number;
    currency?: string;
    isPublic?: boolean;
    isActive?: boolean;
    compatiblePlans?: string[];
    metadata?: any;
}

interface IUpdateAddonDto {
    name?: string;
    description?: string;
    addonType?: AddonType;
    billingPeriod?: 'monthly' | 'annual' | 'quarterly';
    priceCents?: number;
    currency?: string;
    isPublic?: boolean;
    isActive?: boolean;
    compatiblePlans?: string[];
    metadata?: any;
}

// Organisation Addon Types (purchased addons)
type AddonStatus = 'active' | 'expired' | 'canceled';

interface IOrganisationAddon {
    id: string;
    organisationId: number;
    addonId: string;
    addon?: IAddon;
    status: AddonStatus;
    quantity: number;
    purchasedAt: string;
    expiresAt?: string;
    stripeSubscriptionId?: string;
    metadata?: any;
    createdAt: string;
}

interface IAvailableRewardCredit {
    redemptionId: string;
    type: 'credits' | 'subscription_discount' | 'account_credit';
    availableCents: number;
    description: string;
}

interface IAddonPurchasePreview {
    addon: {
        id: string;
        code: string;
        name: string;
        description: string;
        priceCents: number;
        currency: string;
    };
    availableCredits: IAvailableRewardCredit[];
    totalAvailableCredit: number;
    finalPriceCents: number;
    discountApplied: number;
}

interface ICheckoutResult {
    checkoutUrl: string;
    sessionId: string;
}

// On-platform checkout with Payment Element
interface IPaymentIntentResult {
    clientSecret: string;
    paymentIntentId: string;
    amount: number;
    subtotal: number;
    taxAmount: number;
    currency: string;
    addonId: string;
    addonName: string;
    discountApplied: number;
    appliedRedemptionIds: string[];
    requiresAction: boolean;
}

interface IAddonSubscriptionResult {
    subscriptionId: string;
    status: string;
    clientSecret?: string;
    addonId: string;
    addonName: string;
}

interface IAddonPaymentMethod {
    id: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
}

interface ICreateSubscriptionPlanDto {
    code: string;
    name: string;
    description: string;
    billingPeriod: 'monthly' | 'annual' | 'quarterly';
    priceCents: number;
    currency?: string;
    stripePriceId?: string;
    stripeProductId?: string;
    isPublic?: boolean;
    isActive?: boolean;
    displayOrder?: number;
    highlightLabel?: string;
}

interface IUpdateSubscriptionPlanDto {
    name?: string;
    description?: string;
    priceCents?: number;
    isPublic?: boolean;
    isActive?: boolean;
    displayOrder?: number;
    highlightLabel?: string;
}

interface IFeatureDefinition {
    id: string;
    code: string;
    name: string;
    description: string;
    category: string;
    featureType: 'boolean' | 'limit' | 'tier';
    defaultValue: string;
    isActive: boolean;
    metadata?: any;
    createdAt: string;
    updatedAt: string;
}

interface ICreateFeatureDto {
    code: string;
    name: string;
    description: string;
    category: string;
    featureType: 'boolean' | 'limit' | 'tier';
    defaultValue: string;
    isActive?: boolean;
}

interface IUpdateFeatureDto {
    name?: string;
    description?: string;
    category?: string;
    defaultValue?: string;
    isActive?: boolean;
}

interface IPlanEntitlement {
    id: string;
    planId: string;
    featureId: string;
    value: string;
    feature?: IFeatureDefinition;
    createdAt: string;
    updatedAt: string;
}

interface ICreatePlanEntitlementDto {
    planId: string;
    featureId: string;
    value: string;
}

type ValueType = 'set' | 'add' | 'upgrade';

interface IAddonEntitlement {
    id: string;
    addonId: string;
    featureId: string;
    value: string;
    valueType: ValueType;
    feature?: IFeatureDefinition;
    createdAt: string;
}

interface ICreateAddonEntitlementDto {
    featureId: string;
    value: string;
    valueType: ValueType;
}

interface IOrganisationSubscription {
    id: string;
    organisationId: string;
    planId: string;
    plan: ISubscriptionPlan;
    status: 'active' | 'past_due' | 'canceled' | 'paused' | 'trialing' | 'founding_cohort';
    billingCycle: 'monthly' | 'annual' | 'quarterly';
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    foundingCohortSince?: string;
    foundingCohortRequirementsMet?: boolean;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    // Carries pendingPaidPlan (SYR-109) among trial bookkeeping keys.
    metadata?: Record<string, any> | null;
    createdAt: string;
    updatedAt: string;
}

interface IFreeTrialStatus {
    isOnFreeTrial: boolean;
    trialStart: string;
    trialEnd: string;
    daysRemaining: number;
    totalDays: number;
    phase: 'response_collection' | 'active_trial' | 'expired';
    responseRequirement: {
        required: number;
        collected: number;
        met: boolean;
        deadline: string;
        daysRemainingForResponses: number;
        totalDays: number;
    };
}

interface IEntitlementCheckResponse {
    feature: string;
    type: 'boolean' | 'limit' | 'tier';
    hasAccess: boolean;
    limit?: number;
    used?: number;
    remaining?: number;
    percentUsed?: number;
    source?: string;
    tier?: string;
    resetsAt?: string;
}

// Subscription Tier Access Summary Types
interface IRestrictedAllowedElements {
    latentElements: string[];
    questionCategories: string[];
}

interface IBenchmarkingAccessResult {
    overall: boolean;
    productType: boolean;
    allLevels: boolean;
}

interface ITierAccessSummary {
    elementAccess: {
        unrestricted: boolean;
        allowedElements?: IRestrictedAllowedElements;
    };
    benchmarking: IBenchmarkingAccessResult;
    aiExperts: {
        hasAccess: boolean;
        creditsLimit: number;
        unlimited: boolean;
    };
    segmentation: {
        limit: number;
        unlimited: boolean;
    };
}

interface IUsageRecordDto {
    featureCode: string;
    delta: number;
    referenceType: string;
    referenceId: string;
}

interface ICreditBalanceResponse {
    totalCredits: number;
    planIncluded: number;
    purchased: number;
    bonus: number;
    reward: number;
    expiringCredits?: Array<{
        amount: number;
        expiresAt: string;
    }>;
}

interface IConsumeCreditsDto {
    credits: number;
    activityType: string;
    activityId: string;
    description: string;
}

interface IFoundingCohortMember {
    id: string;
    organisationId: string;
    organisation: {
        name: string;
    };
    status: 'active' | 'grace_period' | 'warning' | 'graduated' | 'churned';
    joinedAt: string;
    currentMonthResponses: number;
    consecutiveMonthsMet: number;
    consecutiveMonthsMissed: number;
    gracePeriodsUsed: number;
    graduatedAt?: string;
    churnedAt?: string;
    createdAt: string;
    updatedAt: string;
}

interface IUpdateFoundingCohortStatusDto {
    status: 'active' | 'grace_period' | 'warning' | 'graduated' | 'churned';
    reason?: string;
}

interface IEligibleOrganisation {
    id: number;
    name: string;
    createdAt: string;
    isMock: boolean;
    plan: string;
    userCount: number;
}

interface IBulkAddFoundingCohortResult {
    added: number;
    skipped: number;
    errors: string[];
    members: IFoundingCohortMember[];
}

interface IRewardEarning {
    id: string;
    organisationId: string;
    rewardTierId: string;
    earningPeriodStart: string;
    earningPeriodEnd: string;
    responsesCollected: number;
    rewardValue: number;
    rewardType: 'credits' | 'discount_percentage' | 'account_credit_cents';
    status: 'pending' | 'available' | 'redeemed' | 'expired' | 'forfeited';
    availableAt: string;
    expiresAt: string;
    redeemedAt?: string;
    createdAt: string;
    updatedAt: string;
    // Nested relations (populated when fetched with relations)
    rewardTier?: {
        id: string;
        code: string;
        name: string;
        description?: string;
    };
    organisation?: IOrganisation;
}

interface IRedeemRewardDto {
    earningIds: string[];
    redemptionType: 'credits' | 'subscription_discount' | 'account_credit';
}

// Reward Tier Types
type RewardType = 'credits' | 'discount_percentage' | 'account_credit_cents';
type RewardStatus = 'pending' | 'available' | 'redeemed' | 'expired' | 'forfeited';

interface IRewardTier {
    id: string;
    code: string;
    name: string;
    description?: string;
    minResponses: number;
    maxResponses?: number;
    rewardValue: number;
    rewardType: RewardType;
    isActive: boolean;
    displayOrder: number;
    metadata?: Record<string, any>;
    createdAt: string;
    updatedAt: string;
}

interface ICreateRewardTierDto {
    code: string;
    name: string;
    description?: string;
    minResponses: number;
    maxResponses?: number;
    rewardValue: number;
    rewardType: RewardType;
    isActive?: boolean;
    displayOrder?: number;
    metadata?: Record<string, any>;
}

interface IUpdateRewardTierDto {
    code?: string;
    name?: string;
    description?: string;
    minResponses?: number;
    maxResponses?: number;
    rewardValue?: number;
    rewardType?: RewardType;
    isActive?: boolean;
    displayOrder?: number;
    metadata?: Record<string, any>;
}

interface IGrantRewardDto {
    organisationId: number;
    // Either provide a tierId to use a predefined tier...
    tierId?: string;
    // ...or provide custom reward details for a one-off reward
    customRewardValue?: number;
    customRewardType?: RewardType;
    customRewardName?: string;
    responsesCollected?: number;
    earningPeriodStart?: string;
    earningPeriodEnd?: string;
}

interface IRewardRedemption {
    id: string;
    organisationId: number;
    redemptionType: 'credits' | 'subscription_discount' | 'account_credit';
    totalValue: number;
    creditsGranted?: number;
    discountPercentage?: number;
    accountCreditCents?: number;
    appliedToInvoiceId?: string;
    redeemedBy: number;
    redeemedAt: string;
    metadata?: Record<string, any>;
    createdAt: string;
}

interface IRewardStats {
    totalTiers: number;
    activeTiers: number;
    totalEarnings: number;
    pendingEarnings: number;
    availableEarnings: number;
    redeemedEarnings: number;
    totalRedemptions: number;
    totalValueRedeemed: number;
}

// ===== Customer Segmentation Types =====

type SegmentationFieldType =
    | 'text'
    | 'select'
    | 'number'
    | 'date'
    | 'boolean'
    | 'multi_select'
    | 'currency';

type SegmentationFieldSource = 'manual' | 'csv' | 'shopify' | 'webhook' | 'zapier';

interface ISegmentationFieldMetadata {
    source?: SegmentationFieldSource;
    isPii?: boolean;
    currencyCode?: string;
    dateFormat?: string;
    transformHints?: string[];
}

interface ISegmentationField {
    id: number;
    organisationId: number;
    fieldName: string;
    fieldLabel: string;
    fieldType: SegmentationFieldType;
    options: string[] | null;
    fieldMetadata: ISegmentationFieldMetadata | null;
    isActive: boolean;
    displayOrder: number;
    createdAt: string;
    updatedAt: string;
}

interface ICreateSegmentationFieldDto {
    fieldName: string;
    fieldLabel: string;
    fieldType?: SegmentationFieldType;
    options?: string[];
    fieldMetadata?: ISegmentationFieldMetadata;
    displayOrder?: number;
}

interface IUpdateSegmentationFieldDto {
    fieldLabel?: string;
    fieldType?: SegmentationFieldType;
    options?: string[];
    fieldMetadata?: ISegmentationFieldMetadata;
    isActive?: boolean;
    displayOrder?: number;
}

interface ISegmentationValue {
    id: number;
    customerId: number;
    fieldId: number;
    value: string | null;
    valueText: string | null;
    field?: ISegmentationField;
}

interface ICustomer {
    id: number;
    organisationId: number;
    externalId: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    company: string | null;
    metadata: Record<string, any> | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    segmentationValues?: ISegmentationValue[];
}

interface ICreateCustomerDto {
    externalId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    metadata?: Record<string, any>;
    segmentationValues?: Record<string, string>;
}

interface IUpdateCustomerDto {
    externalId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    metadata?: Record<string, any>;
    isActive?: boolean;
    segmentationValues?: Record<string, string>;
}

interface ICustomerListResponse {
    customers: ICustomer[];
    total: number;
}

interface ISegmentationFieldsLimit {
    limit: number;
    unlimited: boolean;
    used: number;
}

// ===== Customer Import =====

type ImportFileType = 'csv' | 'xlsx';

type ImportState =
    | 'uploaded'
    | 'parsing'
    | 'awaiting_mapping'
    | 'previewing'
    | 'committing'
    | 'completed'
    | 'failed'
    | 'cancelled';

type ImportMatchStrategy = 'email' | 'external_id';

type ImportConflictPolicy = 'skip' | 'overwrite' | 'merge';

type ImportCoreSlot =
    | 'email'
    | 'external_id'
    | 'first_name'
    | 'last_name'
    | 'company';

type ColumnMappingTarget =
    | { kind: 'core'; slot: ImportCoreSlot }
    | { kind: 'field'; fieldName: string }
    | { kind: 'ignore' };

type ImportColumnMapping = Record<string, ColumnMappingTarget>;

interface IImportErrorSample {
    rowNumber: number;
    field: string;
    message: string;
}

interface ICustomerImportJob {
    id: number;
    organisationId: number;
    uploadedByUserId: number;
    fileName: string;
    fileType: ImportFileType;
    state: ImportState;
    matchStrategy: ImportMatchStrategy | null;
    conflictPolicy: ImportConflictPolicy | null;
    headers: string[] | null;
    columnMapping: ImportColumnMapping | null;
    totalRows: number | null;
    validRows: number | null;
    errorRows: number | null;
    errorsSample: IImportErrorSample[] | null;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
}

interface ICustomerImportRow {
    id: string;
    importJobId: number;
    rowNumber: number;
    rawData: Record<string, string>;
    parsedData: Record<string, string | null> | null;
    status: 'pending' | 'valid' | 'invalid' | 'committed' | 'skipped';
    errors: Array<{ field: string; message: string }> | null;
    customerId: number | null;
}

interface ICustomerImportPreview {
    rows: ICustomerImportRow[];
    total: number;
    valid: number;
    invalid: number;
}

interface ISetImportMappingDto {
    columnMapping: ImportColumnMapping;
    matchStrategy: ImportMatchStrategy;
    conflictPolicy: ImportConflictPolicy;
}

// ===== Saved Segments + Cohort Compare =====

type SegmentOperator =
    | 'eq'
    | 'neq'
    | 'in'
    | 'not_in'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'between'
    | 'contains'
    | 'starts_with'
    | 'is_set'
    | 'is_unset';

type MatchLogic = 'all' | 'any';

interface ISegmentFilterCriterion {
    fieldId: number;
    operator: SegmentOperator;
    value?: unknown;
}

// Survey-side segment criteria. Composed AND-wise with filterCriteria.
// Either side may be empty — but at least one must be non-empty.
interface ISegmentSurveyCriteria {
    cohortIds?: number[];
    countryCodes?: string[];
}

interface ISavedSegment {
    id: number;
    organisationId: number;
    brandId: number | null;
    name: string;
    description: string | null;
    filterCriteria: ISegmentFilterCriterion[];
    matchLogic: MatchLogic;
    surveyCriteria: ISegmentSurveyCriteria | null;
    color: string | null;
    lastEvaluatedAt: string | null;
    estimatedSize: number | null;
    createdByUserId: number;
    createdAt: string;
    updatedAt: string;
}

interface ICreateSavedSegmentDto {
    name: string;
    description?: string;
    brandId?: number | null;
    filterCriteria: ISegmentFilterCriterion[];
    matchLogic: MatchLogic;
    surveyCriteria?: ISegmentSurveyCriteria;
    color?: string;
}

interface IUpdateSavedSegmentDto {
    name?: string;
    description?: string;
    brandId?: number | null;
    filterCriteria?: ISegmentFilterCriterion[];
    matchLogic?: MatchLogic;
    surveyCriteria?: ISegmentSurveyCriteria | null;
    color?: string | null;
}

interface ISegmentLibraryUsage {
    used: number;
    limit: number;
    unlimited: boolean;
}

interface ISegmentCriteriaOptions {
    cohorts: ISurveyCohort[];
    countries: string[];
}

// SYR-55 — Segment Comparison on Trends
interface ISegmentComparisonSpec {
    id: string;
    label: string;
    color: string;
    savedSegmentId?: number;
    customerSegmentFilters?: ISegmentFilterCriterion[];
    matchLogic?: MatchLogic;
    cohortIds?: number[];
    countryCodes?: string[];
    isBaseline?: boolean;
}

interface ISegmentComparisonDateRange {
    dateFrom: string;
    dateTo: string;
}

type ISegmentComparisonPeriod =
    | ISegmentComparisonDateRange
    | { kind: 'previous' | 'yoy' };

interface ISegmentComparisonRequest {
    metric: string;
    primaryPeriod: ISegmentComparisonDateRange;
    comparisonPeriod?: ISegmentComparisonPeriod;
    segments: ISegmentComparisonSpec[];
    includeConfidenceIntervals?: boolean;
}

interface ISegmentComparisonBucket {
    index: number;
    label: string;
    n: number;
    mean: number | null;
    lower?: number;
    upper?: number;
}

interface ISegmentComparisonSeries {
    segmentId: string;
    label: string;
    color: string;
    sampleSize: number;
    buckets: ISegmentComparisonBucket[];
    aggregated: { mean: number; n: number };
}

interface ISegmentComparisonResponse {
    primary: ISegmentComparisonSeries[];
    comparison?: ISegmentComparisonSeries[];
    availableMetrics: string[];
    // Response-count thresholds from system settings, bucket-scaled and shared
    // with Overview. Below minResponseThreshold a bucket is gapped; below
    // lowConfidenceThreshold the dot gets an amber ring.
    minResponseThreshold: number;
    lowConfidenceThreshold: number;
}

interface IEvaluateSegmentResponse {
    matchingCustomerCount: number;
    sampleCustomerIds: number[];
}

interface ICohortDefinition {
    customerSegmentFilters?: ISegmentFilterCriterion[];
    matchLogic?: MatchLogic;
    savedSegmentId?: number;
    cohortIds?: number[];
    countryCodes?: string[];
}

interface ICohortCompareRequest {
    cohortA: ICohortDefinition;
    cohortB: ICohortDefinition;
    dateFrom?: string;
    dateTo?: string;
}

interface ICohortDeltas {
    overall: number;
    byElement: Array<{
        elementCode: string;
        a: number;
        b: number;
        delta: number;
    }>;
    byCategory: Array<{
        category: string;
        a: number;
        b: number;
        delta: number;
    }>;
}

type SampleSizeWarningCode =
    | 'low_confidence_a'
    | 'low_confidence_b'
    | 'imbalanced'
    | 'empty_a'
    | 'empty_b';

interface ISampleSizeWarning {
    code: SampleSizeWarningCode;
    message: string;
}

interface ICohortCompareResult {
    a: any; // BrandEquityScoreResult — large existing type
    b: any;
    deltas: ICohortDeltas;
    sampleSizeWarnings: ISampleSizeWarning[];
}

// ===== Customer Integrations =====

type IntegrationType = 'shopify' | 'generic_webhook' | 'zapier';

type IntegrationStatus = 'pending' | 'connected' | 'error' | 'disabled';

type IntegrationCoreSlot =
    | 'email'
    | 'external_id'
    | 'first_name'
    | 'last_name'
    | 'company';

type IntegrationColumnMappingTarget =
    | { kind: 'core'; slot: IntegrationCoreSlot }
    | { kind: 'field'; fieldName: string }
    | { kind: 'ignore' };

type IntegrationColumnMapping = Record<string, IntegrationColumnMappingTarget>;

interface IIntegrationConfig {
    columnMapping?: IntegrationColumnMapping;
    conflictPolicy?: 'skip' | 'overwrite' | 'merge';
    matchStrategy?: 'email' | 'external_id';
    [key: string]: unknown;
}

interface ICustomerIntegration {
    id: number;
    hash: string;
    organisationId: number;
    integrationType: IntegrationType;
    name: string;
    status: IntegrationStatus;
    config: IIntegrationConfig | null;
    hasSecret: boolean;
    lastSyncAt: string | null;
    lastError: string | null;
    createdAt: string;
    updatedAt: string;
    webhookPath: string;
    // Populated only on create / rotate-secret responses. UI surfaces it once.
    signingSecret?: string;
}

interface ICreateIntegrationDto {
    integrationType: IntegrationType;
    name: string;
    config?: IIntegrationConfig;
}

interface IUpdateIntegrationDto {
    name?: string;
    status?: IntegrationStatus;
    config?: IIntegrationConfig;
}

interface IRotateSecretResponse {
    signingSecret: string;
}

type SignupFunnelStageKey =
    | 'registered'
    | 'signedIn'
    | 'onboardingComplete'
    | 'surveyAccessed'
    | 'dashboardUnlocked'
    | 'fullTrialUnlocked'
    | 'subscribed';

interface ISignupFunnelStage {
    key: SignupFunnelStageKey;
    label: string;
    count: number;
}

interface ISignupFunnelOrg {
    id: number;
    hash: string;
    name: string;
    isSuspended: boolean;
    registeredAt: string;
    signedInAt: string | null;
    onboardingCompletedAt: string | null;
    surveyAccessedAt: string | null;
    dashboardUnlockedAt: string | null;
    fullTrialUnlockedAt: string | null;
    subscribedAt: string | null;
    currentStage: SignupFunnelStageKey;
    userCount: number;
    surveyCount: number;
    responseCount: number;
    maxSurveyAccessCount: number;
}

interface ISignupFunnelResponse {
    thresholds: {
        surveyAccessMin: number;
        dashboardMinResponses: number;
        fullTrialMinResponses: number;
    };
    stages: ISignupFunnelStage[];
    orgs: ISignupFunnelOrg[];
}

// Config-driven template type registry (SYR-74).
// The active-list endpoint returns resolved FK ids; the admin endpoint returns
// the raw row (with id) — this interface is the superset of both.
export interface IQuestionTemplateType {
    id?: number;
    slug: string;
    label: string;
    productType: string | null;
    benchmarkBusinessTypeSlug: string | null;
    benchmarkProductTypeSlug: string | null;
    businessTypeId?: number | null;
    benchmarkProductTypeId?: number | null;
    isBuiltIn: boolean;
    isActive: boolean;
    displayOrder: number;
}

export interface IQuestionTemplateTypeInput {
    slug: string;
    label: string;
    productType?: string | null;
    benchmarkBusinessTypeSlug?: string | null;
    benchmarkProductTypeSlug?: string | null;
    isBuiltIn?: boolean;
    isActive?: boolean;
    displayOrder?: number;
}

// QUALIFICATION_CONFIG (signup quiz, Mode A). Mirrors the API interface.
export interface IQualificationOption {
    value: string;
    label: string;
}

export interface IQualificationCombination {
    // Absent for known-but-not-yet-supported combinations (supported: false),
    // which capture to the waitlist rather than resolve a template.
    templateType?: string;
    productType?: string;
    // Explicitly mark a (model, category) as not supported yet → waitlist. Its
    // presence still makes the category ask the business model.
    supported?: boolean;
}

export interface IQualificationReverseDefault {
    businessModel?: string;
    productCategory?: string;
    productCategoryLabel?: string;
}

export interface IQualificationConfig {
    businessModels: IQualificationOption[];
    productCategories: IQualificationOption[];
    combinations: Record<string, Record<string, IQualificationCombination>>;
    // Canonical (businessModel, productCategory) to attribute to an org created
    // from a templateType alone (agency/invite). Keyed by templateType.
    reverseDefaults?: Record<string, IQualificationReverseDefault>;
}

// Embedded CRM (SYR-84). Mirrors src/crm/dto in the API.
export interface ICrmPipelineStage {
    id: number;
    code: string;
    label: string;
    color: string;
    displayOrder: number;
    isWon: boolean;
    isLost: boolean;
    isActive: boolean;
}

export interface ICrmContact {
    id: number;
    crmCompanyId: number;
    name: string | null;
    email: string | null;
    position: string | null;
    phone: string | null;
    linkedinUrl: string | null;
    isPrimary: boolean;
    userId: number | null;
}

export type CrmLifecycleStage =
    | 'prospect'
    | 'registered'
    | 'signedIn'
    | 'onboardingComplete'
    | 'surveyAccessed'
    | 'dashboardUnlocked'
    | 'fullTrialUnlocked'
    | 'subscribed'
    | 'churnRisk'
    | 'churned'
    | 'foundingCohort'
    | 'founderLapsed';

export interface ICrmCompany {
    id: number;
    hash: string;
    name: string;
    website: string | null;
    emailDomain: string | null;
    productType: string | null;
    organisationId: number | null;
    organisationName: string | null;
    organisationHash: string | null;
    pipelineStage: ICrmPipelineStage;
    lifecycleStage: CrmLifecycleStage;
    ownerUserId: number | null;
    ownerName: string | null;
    source: 'sheet' | 'signup' | 'manual' | 'charter';
    sheetRowKey: string | null;
    removedFromSheet: boolean;
    holdReason: string | null;
    lostReason: string | null;
    sheetNotes: string | null;
    healthScore: number | null;
    metadata: Record<string, any> | null;
    isArchived: boolean;
    primaryContact: ICrmContact | null;
    openTaskCount: number;
    nextTaskDueAt: string | null;
    openReminderCount: number;
    nextReminderDueAt: string | null;
    lastActivityAt: string | null;
    stageEnteredAt: string;
    createdAt: string;
    updatedAt: string;
}

export interface ICrmUsageSnapshot {
    lastLoginAt: string | null;
    userCount: number;
    brandCount: number;
    surveyCount: number;
    responseCount: number;
    responsesLast30Days: number;
    responsesPrior30Days: number;
}

export interface ICrmSubscriptionSnapshot {
    planCode: string | null;
    planName: string | null;
    status: string | null;
    billingCycle: string | null;
    priceCents: number | null;
    trialStart: string | null;
    trialEnd: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean | null;
}

export interface ICrmEmailEngagement {
    sendsLast30Days: number;
    opensLast30Days: number;
    clicksLast30Days: number;
    lastOpenAt: string | null;
}

export interface ICrmCompanyDetail extends ICrmCompany {
    contacts: ICrmContact[];
    funnel: ISignupFunnelOrg | null;
    usage: ICrmUsageSnapshot | null;
    subscription: ICrmSubscriptionSnapshot | null;
    emailEngagement: ICrmEmailEngagement | null;
}

export interface ICrmActivity {
    id: number;
    crmCompanyId: number;
    crmContactId: number | null;
    type: string;
    body: string | null;
    metadata: Record<string, any> | null;
    createdByUserId: number | null;
    createdByName: string | null;
    occurredAt: string;
    createdAt: string;
}

export interface ICrmInboundAttachment {
    filename: string;
    s3Key: string;
    size: number;
    contentType: string;
}

export interface ICrmInboundEmail {
    id: number;
    imapUid: number;
    messageId: string;
    fromEmail: string;
    fromName: string | null;
    toJson: string[] | null;
    ccJson: string[] | null;
    subject: string | null;
    bodyHtml: string | null;
    bodyText: string | null;
    strippedHtml: string | null;
    strippedText: string | null;
    attachmentsJson: ICrmInboundAttachment[] | null;
    receivedAt: string;
    crmCompanyId: number | null;
    crmCompany: { id: number; name: string } | null;
    crmActivityId: number | null;
    matchMethod: 'message_id' | 'contact_email' | 'platform_user' | 'dist_list' | 'domain' | 'subject' | 'manual' | null;
    matchStatus: 'matched' | 'flagged' | 'ignored';
    candidatesJson: { companyId: number; name: string; reason: string }[] | null;
    ignoreReason: string | null;
    createdAt: string;
}

export interface ICrmInboxSyncResult {
    fetched: number;
    matched: number;
    flagged: number;
    ignored: number;
    skipped: number;
    rematched: number;
}

export interface ICrmTask {
    id: number;
    crmCompanyId: number;
    companyName: string;
    title: string;
    details: string | null;
    dueAt: string | null;
    status: 'open' | 'done' | 'cancelled';
    assignedToUserId: number | null;
    assignedToName: string | null;
    createdByUserId: number | null;
    completedAt: string | null;
    reminderEnabled: boolean;
    reminderSentAt: string | null;
    createdAt: string;
}

export interface ICrmReminder {
    id: number;
    crmCompanyId: number;
    companyName: string;
    title: string;
    details: string | null;
    dueAt: string;
    reminderSentAt: string | null;
}

export interface ICrmLinkSuggestion {
    prospectCompanyId: number;
    prospectCompanyName: string;
    signupCompanyId: number;
    signupCompanyName: string;
    organisationId: number;
    organisationName: string | null;
    matchType: 'email' | 'domain';
    matchValue: string;
}

export interface ICrmSyncRun {
    id: number;
    startedAt: string;
    finishedAt: string | null;
    status: string;
    rowsPulled: number;
    rowsPushed: number;
    rowsCreated: number;
    conflicts: number;
    error: string | null;
}

export interface ICrmSyncStatus {
    enabled: boolean;
    configured: boolean;
    intervalMinutes: number;
    lastRuns: ICrmSyncRun[];
}

// ---------------------------------------------------------------- Platform Mail

/** CRM side of a mail thread. Account and organisation are different things:
 *  an account exists from prospect stage, an organisation means they signed up. */
export interface IMailAccount {
    crmCompanyId: number;
    companyName: string;
    companyHash: string | null;
    organisationId: number | null;
    organisationName: string | null;
}

export interface IMailSender {
    userId: number;
    name: string;
    firstName: string;
    email: string;
    fromLabel: string;
    address: string;
    usesOwnAddress: boolean;
}

export interface IMailAttachment {
    filename: string;
    size: number;
    contentType: string;
    /** Short-lived signed link; null when the file was never stored. */
    url: string | null;
}

export interface IMailMessage {
    id: string;
    direction: 'out' | 'in';
    messageId: string;
    from: string;
    fromName?: string | null;
    to: string[];
    cc: string[];
    subject: string;
    bodyHtml: string | null;
    strippedHtml?: string | null;
    attachments: IMailAttachment[];
    occurredAt: string;
    sentByUserId?: number;
    assignedUserId?: number | null;
    crmCompanyId?: number | null;
}

export interface IMailRecipient {
    email: string;
    name: string | null;
    source: 'platform_user' | 'crm_contact' | 'correspondence';
}

export interface IMailThreadSummary {
    threadKey: string;
    subject: string;
    participants: string[];
    messageCount: number;
    lastMessageAt: string;
    lastDirection: 'out' | 'in';
    assignedUserId: number | null;
    crmCompanyId: number | null;
    account: IMailAccount | null;
    unassignedInbound: number;
    unread: boolean;
    followUp: boolean;
    hasAttachments: boolean;
    archived: boolean;
}

export interface IMailThread {
    threadKey: string;
    subject: string;
    account: IMailAccount | null;
    messages: IMailMessage[];
}

export interface IMailSyncStatus {
    enabled: boolean;
    intervalMinutes: number;
    lastSyncAt: string | null;
}

export interface IMailStagedAttachment {
    id: string;
    filename: string;
    size: number;
    contentType: string;
}

export interface ICrmFunnelOrg extends ISignupFunnelOrg {
    crmCompanyId: number | null;
    crmCompanyHash: string | null;
}

export interface ICrmFunnelResponse {
    thresholds: ISignupFunnelResponse['thresholds'];
    stages: ISignupFunnelResponse['stages'];
    orgs: ICrmFunnelOrg[];
}

export interface ICrmActivationAccount {
    crmCompanyId: number;
    name: string;
    source: string;
    lifecycleStage: CrmLifecycleStage;
    signedUpAt: string;
    firstBlastAt: string | null;
    daysToFirstBlast: number | null;
    blastStarts30d: number;
    firstWorkflowStartAt: string | null;
    lastWorkflowStartAt: string | null;
    workflowStarts30d: number;
    workflowActive: boolean;
    responseCount: number;
}

export interface ICrmStageFlow {
    code: string;
    label: string;
    color: string;
    entered: number;
    advanced: number;
    lostAfter: number;
    currentlyIn: number;
    conversionRate: number | null;
    avgDaysInStage: number | null;
    medianDaysInStage: number | null;
}

export interface ICrmReports {
    activation: {
        accounts: ICrmActivationAccount[];
        funnel: {
            signedUp: number;
            firstBlast: number;
            workflowStarted: number;
            workflowActive: number;
        };
        avgDaysToFirstBlast: number | null;
        medianDaysToFirstBlast: number | null;
    };
    sales: {
        won: number;
        lost: number;
        avgDaysToWin: number | null;
        mrrWonMonthly: number;
        lostReasons: { reason: string; count: number }[];
        mrrBySource: { source: string; accounts: number; mrrMonthly: number }[];
        stageFlow: ICrmStageFlow[];
    };
}

// ---------------------------------------------------------------------------
// SYR-105 Auto-Concierge
// ---------------------------------------------------------------------------

export type ConciergePackStatus =
    | 'QUEUED_RESEARCH'
    | 'RESEARCHING'
    | 'GENERATING'
    | 'DRAFT'
    | 'APPROVED'
    | 'SENT'
    | 'KILLED'
    | 'FAILED'
    | 'SKIPPED';

export interface IConciergeAudience {
    surveyHash: string;
    surveyName: string;
    cohortCode: string | null;
    kind: 'past' | 'recent';
    recommended: boolean;
    guidance: string;
}

export interface IConciergePackDraft {
    researchSummary: string;
    voiceNotes: string;
    customerBaseSignal: string | null;
    espGuess: string | null;
    offerDescription: string;
    incentiveRationale: string;
    incentiveIsGiftCardDraw: boolean;
    customerEmailSubject: string;
    customerEmailBody: string;
    coveringEmailSubject: string;
    coveringEmailBodyHtml: string;
    // Real per-respondent statement count, from survey_setting.
    questionsPerResponse: number | null;
    audiences: IConciergeAudience[];
    thankYouButtonUrl: string | null;
    utmCampaign: string;
    flags: {
        regulated: boolean;
        gifting: boolean;
        lowRepurchase: boolean;
        noBackCatalogue: boolean;
        multiMarket: boolean;
        smallCustomerBase: boolean;
        notes: string | null;
    };
}

export interface IConciergeLintViolation {
    field: string;
    rule: string;
    detail: string;
    excerpt: string | null;
}

export interface IConciergePack {
    id: number;
    organisationId: number;
    brandId: number;
    subjectId: number | null;
    crmCompanyId: number | null;
    status: ConciergePackStatus;
    triggerSource: 'wizard' | 'admin';
    lintJson: IConciergeLintViolation[] | null;
    attemptCount: number;
    lastError: string | null;
    generatedAt: string | null;
    editedAt: string | null;
    approvedAt: string | null;
    approvedByUserId: number | null;
    sentAt: string | null;
    sentToEmail: string | null;
    killedAt: string | null;
    killReason: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface IConciergePackListRow {
    pack: IConciergePack;
    organisationName: string;
    brandName: string;
    identityVerificationStatus: string;
    lintCount: number;
}

export interface IConciergeDecisionSignals {
    contactEmail: string | null;
    contactEmailDomain: string | null;
    brandDomain: string | null;
    domainsMatch: boolean;
    isFreeMailDomain: boolean;
    identityVerificationStatus: string;
    events30d: number | null;
    activeDays30d: number | null;
    lastSeenAt: string | null;
    topPages: { path: string; count: number }[];
    postHogAvailable: boolean;
}

export interface IConciergePackDetail {
    pack: IConciergePack;
    draft: IConciergePackDraft | null;
    // Resolved server-side by the same rules the send uses. Optional because a
    // cached response from before this field existed will not carry it.
    sendPreview?: {
        fromLabel: string | null;
        fromAddress: string | null;
        to: string | null;
        contactName: string | null;
    };
    organisationName: string;
    brandName: string;
    websiteUrl: string | null;
    signals: IConciergeDecisionSignals;
    emailPreview: { subject: string; bodyHtml: string } | null;
    emailPreviewError: string | null;
    surveyLinks: Array<{
        surveyHash: string;
        surveyName: string;
        kind: 'past' | 'recent';
        recommended: boolean;
        bareUrl: string;
        testUrl: string;
    }>;
}

// SYR-105 phase 2 — the brand-facing offer screen.
export interface IConciergeOfferContext {
    brandName: string;
    organisationName: string;
    destinationUrl: string | null;
    reviewUrl: string | null;
    showDestinationButton: boolean;
    showReviewButton: boolean;
    showReward: boolean;
    destinationButtonText: string | null;
    reviewButtonText: string | null;
    // The past-customer survey's 5-colour palette, so the preview shows the
    // brand's actual thank-you page rather than a generic dark card.
    theme: {
        primaryColor?: string;
        secondaryColor?: string;
        backgroundColor?: string;
        cardColor?: string;
        accentColor?: string;
    } | null;
    discountCode: string | null;
    offerDescription: string | null;
    offerTerms: string | null;
    completionTitle: string | null;
    completionSubtitle: string | null;
    recommendedOffer: string | null;
    surveyCount: number;
    alreadySet: boolean;
}

export interface ISaveConciergeOffer {
    showDestinationButton?: boolean;
    showReviewButton?: boolean;
    showReward?: boolean;
    destinationButtonText?: string | null;
    reviewButtonText?: string | null;
    completionTitle?: string | null;
    completionSubtitle?: string | null;
    discountCode?: string | null;
    offerDescription?: string | null;
    offerTerms?: string | null;
    destinationUrl?: string | null;
    reviewUrl?: string | null;
}

// ---- Admin Live Analytics (GA real-time style) -----------------------------
export type LiveSurface = 'website' | 'app' | 'survey' | 'server' | 'other';

export type LiveMilestone =
    | 'signup_cta'
    | 'signed_up'
    | 'logged_in'
    | 'hypothesis_requested'
    | 'hypothesis_verified'
    | 'hypothesis_ready'
    | 'hypothesis_viewed'
    | 'hypothesis_cta'
    | 'survey_submitted'
    | 'charter_applied';

export interface ILivePageHit {
    timestamp: string;
    surface: LiveSurface;
    host: string | null;
    path: string;
}

export interface ILiveVisitor {
    key: string;
    distinctIds: string[];
    identityKind: 'user' | 'lead' | 'anonymous';
    identity: string | null;
    identityLabel: string | null;
    organisationId: number | null;
    organisationName: string | null;
    isTeam: boolean;
    surfaces: LiveSurface[];
    country: string | null;
    countryCode: string | null;
    region: string | null;
    city: string | null;
    device: string | null;
    browser: string | null;
    os: string | null;
    referringDomain: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    firstSeen: string;
    lastSeen: string;
    isActive: boolean;
    eventCount: number;
    pageviewCount: number;
    platformEventCount: number;
    currentSurface: LiveSurface;
    currentHost: string | null;
    currentPath: string | null;
    journey: ILivePageHit[];
    milestones: LiveMilestone[];
}

export interface ILiveEvent {
    id: string;
    timestamp: string;
    source: 'posthog' | 'platform';
    surface: LiveSurface;
    event: string;
    host: string | null;
    path: string | null;
    detail: string | null;
    visitorKey: string | null;
    identity: string | null;
    organisationName: string | null;
    country: string | null;
    city: string | null;
    isTeam: boolean;
}

export interface ILiveAnalyticsSnapshot {
    enabled: boolean;
    generatedAt: string;
    windowMinutes: number;
    activeWindowMinutes: number;
    activeNow: number;
    visitorsInWindow: number;
    pageviewsInWindow: number;
    signupsInWindow: number;
    hypothesisRequestsInWindow: number;
    perMinute: { minute: string; visitors: number; pageviews: number }[];
    surfaces: { surface: LiveSurface; visitors: number }[];
    topPages: { surface: LiveSurface; host: string | null; path: string; visitors: number; pageviews: number }[];
    locations: { country: string | null; countryCode: string | null; city: string | null; visitors: number }[];
    sources: { referringDomain: string; visitors: number }[];
    visitors: ILiveVisitor[];
    events: ILiveEvent[];
}
